/**
 * HubPage — Main UI
 *
 * Three-layer site-visit experience:
 *   A. Comparison anchor  — current vs recommended (always visible)
 *   B. Explainer cards    — pulled, not pushed; mark ⭐ / 📄
 *   C. Scope awareness    — inline "this assumes X / rules out Y"
 *
 * Two pages (tabs):
 *   1. System Choice — tiles, option cards, explainers
 *   2. Explainers — searchable technical/customer explainers
 *
 * Persists to D1 via existing document persistence utilities.
 * Produces documents loadable by the portal at /portal/view/:docId.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type {
  HeatDemandData,
  SystemDetailsData,
  WaterData,
  EmittersData,
  DHWPressureData,
  SystemOptionCard as OptionCardData,
  ConfidenceBadge,
} from './types';
import { TopStrip } from './TopStrip';
import { HeatDemandTile } from './tiles/HeatDemandTile';
import { SystemDetailsTile } from './tiles/SystemDetailsTile';
import { WaterTile } from './tiles/WaterTile';
import { EmittersTile } from './tiles/EmittersTile';
import { DHWPressureTile } from './tiles/DHWPressureTile';
import { PrioritiesTile } from './tiles/PrioritiesTile';
import { deriveHeatLossBand } from './derivations';
import { HeatDemandScreen } from './screens/HeatDemandScreen';
import { SystemDetailsScreen } from './screens/SystemDetailsScreen';
import { WaterScreen } from './screens/WaterScreen';
import { EmittersScreen } from './screens/EmittersScreen';
import { DHWPressureScreen } from './screens/DHWPressureScreen';
import PrioritiesScreen from './screens/PrioritiesScreen';
import { SystemOptionsArea } from './output/SystemOptionsArea';
import { CostChartsPanel } from './CostChartsPanel';
import { ComparisonAnchor } from './ComparisonAnchor';
import { ExplainerCard } from './ExplainerCard';
import { ExplainersPage } from '../explainers/ExplainersPage';
import {
  DEFAULT_EXPLAINERS,
  markViewed,
  markStarred,
  markInReport,
  getStarredExplainers,
  serializeExplainerState,
  rehydrateExplainerState,
  type Explainer,
} from './explainerRegistry';
import { ConfidenceLegend } from './components/ConfidenceLegend';
import {
  getDefaultOptionalItems,
  toggleOptionalItem,
  buildScopeSnapshot,
  type ScopeSnapshot,
} from './scopeModel';
import { buildStorySnapshot, type StorySnapshot } from '../../utils/storySnapshot';
import {
  persistDocument,
  updateDocument,
  retrieveDocument,
  type PersistResponse,
} from '../../utils/documentPersistence';
import { runDecisionEngine, type DecisionEngineResult } from '../../api/decisionEngine';
import type { CollectedData } from '../../context/RecommendationContext';
import type { ExplanationItem, ScopeItem } from './types';
import type { SystemRecInput, RecommendationOption, ConfidenceSummary, RedFlag, FuturePlan } from '@core/types';
import { notifyModalOpened, notifyModalClosed, shouldShowDevtools } from '../../devtools';
import { updateRawInputSnapshot, trackLifecycleEvent, inferBoilerTypeFromHub } from '../../mapping';
import { applyEmitterDefault } from './emitterDefaults';
import { buildEvaluationContext, deriveTechnicalFit, futureProofingScore, runningCostPriorityScore, standingLossScore, standingLossDescription, cylinderRetentionDescription } from './evaluationContext';
import { PrintableAssessmentReportV2 } from '../report/PrintableAssessmentReportV2';
import { OpenPortalButton } from '../portal/OpenPortalButton';
import { makePortalUrl } from '../portal/makePortalUrl';

function isDevtoolsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return shouldShowDevtools();
}

// ── Types ───────────────────────────────────────────────────────────

type ActiveScreen = 'none' | 'heat-demand' | 'system-details' | 'water' | 'emitters' | 'dhw-pressure' | 'priorities';
type ActivePage = 'system-choice' | 'explainers';

const DEFAULT_VISIBLE_EXPLANATION_ITEMS = 6;

function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent Fit';
  if (score >= 75) return 'Strong Fit';
  if (score >= 60) return 'Acceptable with Trade-offs';
  return 'Significant Compromises';
}

const KIND_SEVERITY_WEIGHT = {
  critical: 3,
  warning: 2,
  info: 1,
} as const;

function dedupeExplanationItems(items: ExplanationItem[]): ExplanationItem[] {
  const seen = new Set<string>();
  const deduped: ExplanationItem[] = [];

  for (const item of items) {
    const normalizedText = item.text.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalizedText) continue;

    const key = item.sourceKey ? `source:${item.sourceKey}` : `text:${normalizedText}`;
    if (seen.has(key)) continue;

    seen.add(key);
    deduped.push({ ...item, text: item.text.trim() });
  }

  return deduped;
}

function buildExplanationItems(option: RecommendationOption, extras: {
  reasons: string[];
  assumptions: string[];
  changes: string[];
}): ExplanationItem[] {
  const breakdown = option.fitScore?.breakdown;
  const penalties = (breakdown as { penalties?: Array<{ dimension: string; penalty: number; reason: string }> } | undefined)?.penalties ?? [];
  const constraintReasons = breakdown?.constraintReasons ?? [];
  const confidenceAdjustments = breakdown?.confidenceAdjustments ?? [];

  const items: ExplanationItem[] = [];
  let seq = 0;
  const add = (partial: Omit<ExplanationItem, 'id'>) => {
    seq += 1;
    items.push({ id: `${option.id || option.boiler}-${seq}`, ...partial });
  };

  for (const text of option.whyThisWon || []) {
    add({ text, kind: 'benefit', severity: 'info', sourceKey: 'whyThisWon' });
  }
  for (const text of option.pros || []) {
    add({ text, kind: 'benefit', severity: 'info', sourceKey: 'pros' });
  }
  if (option.why) {
    add({ text: option.why, kind: 'note', severity: 'info', sourceKey: 'why' });
  }
  for (const text of breakdown?.reasons ?? []) {
    add({ text, kind: 'benefit', severity: 'info', sourceKey: 'fit.breakdown.reasons' });
  }
  for (const text of extras.reasons) {
    add({ text, kind: 'note', severity: 'info', sourceKey: 'derived.reasons' });
  }

  for (const text of option.cons || []) {
    add({ text, kind: 'cost', severity: 'warning', sourceKey: 'cons' });
  }
  for (const penalty of penalties) {
    add({
      text: `${penalty.dimension}: ${penalty.reason}`,
      kind: 'cost',
      severity: penalty.penalty <= -20 ? 'critical' : 'warning',
      impact: penalty.penalty,
      sourceKey: `penalty.${penalty.dimension}`,
    });
  }

  for (const reason of constraintReasons) {
    add({
      text: `${reason.label}${reason.details ? ` — ${reason.details}` : ''}`,
      kind: reason.kind === 'hard' ? 'cost' : 'assumption',
      severity: reason.kind === 'hard' ? 'critical' : 'warning',
      impact: reason.kind === 'hard' ? -100 : -25,
      sourceKey: `constraint.${reason.id}`,
    });
  }

  for (const text of extras.changes) {
    add({ text, kind: 'work', severity: 'warning', sourceKey: 'derived.work' });
  }

  for (const text of confidenceAdjustments) {
    add({ text, kind: 'assumption', severity: 'warning', sourceKey: 'fit.breakdown.confidenceAdjustments' });
  }
  if (option.exclusionReason) {
    add({ text: option.exclusionReason, kind: 'assumption', severity: 'warning', sourceKey: 'exclusionReason' });
  }
  for (const text of extras.assumptions) {
    add({ text, kind: 'assumption', severity: 'warning', sourceKey: 'derived.assumptions' });
  }

  return dedupeExplanationItems(items);
}

function rankExplanationItems(items: ExplanationItem[]): ExplanationItem[] {
  return [...items].sort((a, b) => {
    const sevDiff = KIND_SEVERITY_WEIGHT[b.severity] - KIND_SEVERITY_WEIGHT[a.severity];
    if (sevDiff !== 0) return sevDiff;

    const impactA = Math.abs(a.impact ?? 0);
    const impactB = Math.abs(b.impact ?? 0);
    if (impactB !== impactA) return impactB - impactA;

    return a.text.localeCompare(b.text);
  });
}

// ── Validation ──────────────────────────────────────────────────────

interface ValidationResult {
  isValid: boolean;
  missingFields: string[];
}

function validateFormData(
  heatDemand: HeatDemandData,
  systemDetails: SystemDetailsData,
  water: WaterData,
  emitters: EmittersData,
  dhwPressure: DHWPressureData,
  priorities: Array<{ key: string; rank: number }>,
  selectedOptionA: string | null
): ValidationResult {
  const missingFields: string[] = [];

  // Check Heat Demand - dwelling form and bedrooms are required minimum
  if (heatDemand.dwellingForm === 'unknown') {
    missingFields.push('Heat Demand: Dwelling type');
  }
  // bedrooms is typed as string in HeatDemandData
  if (heatDemand.bedrooms === '' || heatDemand.bedrooms === undefined) {
    missingFields.push('Heat Demand: Number of bedrooms');
  }

  // Check System Details - at least heat source and DHW architecture
  if (systemDetails.heatSource === 'unknown') {
    missingFields.push('System Details: Heat source');
  }
  if (systemDetails.dhwArchitecture === 'unknown') {
    missingFields.push('System Details: DHW architecture');
  }

  // Check Water - basic occupancy needed (occupants is typed as string)
  if (water.occupants === '' || water.occupants === undefined) {
    missingFields.push('Water/DHW: Number of occupants');
  }

  // Check Emitters - emitter type needed
  if (emitters.emitters === 'unknown') {
    missingFields.push('Emitters: Emitter type');
  }

  // Check if at least one option is selected
  if (!selectedOptionA) {
    missingFields.push('System Option: Select at least one system option');
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

// ── Defaults ────────────────────────────────────────────────────────

const DEFAULT_CONFIDENCE: ConfidenceBadge = { level: 'unknown', label: 'not started' };

const DEFAULT_HEAT_DEMAND: HeatDemandData = {
  dwellingForm: 'unknown', bedrooms: '', bathrooms: '', fabricEra: 'unknown', retrofitLevel: 'unknown',
  constructionArchetype: 'standard', heatLossBand: 'unknown', confidence: { ...DEFAULT_CONFIDENCE },
};
const DEFAULT_SYSTEM_DETAILS: SystemDetailsData = {
  heatSource: 'unknown', dhwArchitecture: 'unknown', circuitType: 'unknown',
  controlTopology: 'unknown', emitters: 'unknown', lowTempDesign: 'unknown',
  lowTempEvidence: '', cylinderSpace: 'unknown', gcNumber: '', boilerMake: '', boilerModel: '',
  boilerOutput: '', sedbukBand: '', sedbukRating: '', systemAge: '',
  temperatureControls: 'unknown',
  confidence: { ...DEFAULT_CONFIDENCE },
};
const DEFAULT_WATER: WaterData = {
  dhwSystemType: 'unknown', dhwHeatSource: 'unknown', cylinderVolumeBand: 'unknown',
  cylinderTypeProfile: 'standard-modern', // Default to modern cylinder for new installs (not legacy)
  standingLoss: '1.2 – 1.6 kWh/day', standingLossBand: 'medium', cylinderEra: 'green',
  currentStandingLossKwhPerDay: 1.5, standingLossSource: 'assumed_default',
  circulationMode: 'unknown', pipeRunBand: 'unknown', pipeInsulation: '',
  hardnessBand: 'unknown', scaleProfile: 'unknown', occupants: '',
  confidence: { ...DEFAULT_CONFIDENCE },
};
const DEFAULT_EMITTERS: EmittersData = {
  emitters: 'unknown', performanceSignal: 'unknown', lowTempCapability: 'unknown',
  sludgeEvidence: 'unknown',
  lowTempEvidence: '', primarySizeBand: 'unknown', layoutType: 'unknown',
  pumpingArrangement: 'unknown', hydraulicSeparation: 'unknown', confidence: { ...DEFAULT_CONFIDENCE },
};
const DEFAULT_DHW_PRESSURE: DHWPressureData = {
  coldMainType: 'unknown',
  mainsFlowLpm: null,
  mainsStaticPressureBar: null,
  mainsDynamicFlowLpm: null,
  mainsDynamicPressureBar: null,
  confidence: { ...DEFAULT_CONFIDENCE },
};

// ── System option evaluation (placeholder) ──────────────────────────

function deriveSystemOptions(
  hd: HeatDemandData, sd: SystemDetailsData, wd: WaterData, em: EmittersData, dhwp: DHWPressureData,
  priorities: Array<{ key: string; rank: number }>,
): OptionCardData[] {
  const devtoolsEnabled = isDevtoolsEnabled();

  // Thresholds for mains adequacy
  const COMBI_MIN_FLOW_RATE = 12; // L/min
  const COMBI_MIN_PRESSURE = 2.0; // bar
  const UNVENTED_MIN_FLOW_RATE = 15; // L/min
  const UNVENTED_MIN_PRESSURE = 1.5; // bar

  // Cold mains assessment
  const mainsFlow = dhwp.mainsFlowLpm;
  const mainsStaticPressure = dhwp.mainsStaticPressureBar;
  const mainsDynamicFlow = dhwp.mainsDynamicFlowLpm;
  const mainsDynamicPressure = dhwp.mainsDynamicPressureBar;

  const coldMainsAssessed = mainsFlow !== null && mainsStaticPressure !== null;
  const dynamicPairAssessed = mainsDynamicFlow !== null && mainsDynamicPressure !== null;
  const mainsSupportsCombi = coldMainsAssessed && mainsStaticPressure >= COMBI_MIN_PRESSURE && mainsFlow >= COMBI_MIN_FLOW_RATE;
  const mainsSupportsUnvented = coldMainsAssessed && mainsStaticPressure >= UNVENTED_MIN_PRESSURE && mainsFlow >= UNVENTED_MIN_FLOW_RATE;

  const confidence = coldMainsAssessed ? (dynamicPairAssessed ? 'high' : 'medium') : 'low';

  // Emitter & fabric signals
  const lowTempReady = em.lowTempCapability === 'yes' || sd.lowTempDesign === 'yes';
  const highHeatLoss = hd.heatLossBand === 'high' || hd.heatLossBand === 'very-high';
  const hasCylinderConflict =
    (wd.circulationMode === 'bronze-return-24-7' || wd.circulationMode === 'bronze-return-timed')
    && wd.pipeRunBand === 'long';

  // Data completeness factor for live % match
  const fields = [
    hd.dwellingForm !== 'unknown',
    hd.bedrooms !== '',
    hd.bathrooms !== '',
    hd.fabricEra !== 'unknown',
    hd.retrofitLevel !== 'unknown',
    sd.heatSource !== 'unknown',
    sd.circuitType !== 'unknown',
    wd.dhwSystemType !== 'unknown',
    em.emitters !== 'unknown',
    coldMainsAssessed,
    dynamicPairAssessed,
    sd.temperatureControls !== 'unknown',
    em.lowTempCapability !== 'unknown',
  ];
  const completeness = fields.filter(Boolean).length / fields.length;
  const scaleScore = (raw: number) => Math.round(raw * (0.5 + 0.5 * completeness));

  const circuitTypeKnown = sd.circuitType !== 'unknown';

  // ── Derive current system category for like-for-like comparison ────
  // Maps the surveyed system to one of our 12 option IDs
  type SystemCategory =
    | 'regular-open-vented' | 'regular-unvented'
    | 'system-open-vented' | 'system-unvented'
    | 'combi' | 'storage-combi'
    | 'ashp' | 'gshp'
    | 'unknown';

  const deriveCurrentCategory = (): SystemCategory => {
    if (sd.heatSource === 'heat-pump') return 'ashp'; // can't distinguish ASHP/GSHP here
    if (sd.dhwArchitecture === 'combi-on-demand') return 'combi';
    // Stored DHW: use circuit type to distinguish regular vs system
    if (sd.circuitType === 'open-vented') {
      if (wd.dhwSystemType === 'unvented-cylinder') return 'regular-unvented';
      return 'regular-open-vented';
    }
    if (sd.circuitType === 'sealed') {
      if (wd.dhwSystemType === 'unvented-cylinder') return 'system-unvented';
      return 'system-open-vented';
    }
    return 'unknown';
  };
  const currentCat = deriveCurrentCategory();

  // Disruption scoring: like-for-like = 95, same boiler family = 80,
  // different boiler type = 55, fuel-change = 35, heat pump = 25
  const disruptionScore = (optionId: string): number => {
    if (currentCat === 'unknown') return 65; // can't compare, neutral
    // Like-for-like (ignoring Mixergy variant)
    const normalise = (id: string) => id.replace('mixergy-', '').replace('storage-', '');
    if (normalise(optionId) === normalise(currentCat)) return 95;
    // Same boiler family (e.g. regular vented → regular unvented)
    const optFamily = optionId.split('-')[0]; // regular / system / combi / ashp / gshp
    const curFamily = currentCat.split('-')[0];
    if (optFamily === curFamily) return 80;
    // Boiler-to-boiler (regular ↔ system ↔ combi)
    const boilerFamilies = ['regular', 'system', 'combi', 'storage'];
    if (boilerFamilies.includes(optFamily) && boilerFamilies.includes(curFamily)) return 55;
    // Fuel change / heat pump transition
    return optionId.startsWith('ashp') || optionId.startsWith('gshp') ? 25 : 40;
  };

  // ── Cylinder space feasibility ─────────────────────────────────────
  const hasCylinderSpace = sd.cylinderSpace === 'yes-existing' || sd.cylinderSpace === 'yes-available';
  const noCylinderSpace = sd.cylinderSpace === 'no';
  const cylinderSpaceUnknown = sd.cylinderSpace === 'unknown';
  // Options that need a cylinder
  const needsCylinder = (id: string) =>
    id.includes('vented') || id.includes('unvented') || id.includes('ashp') || id.includes('gshp');

  // ── Technical sub-score helpers ──────────────────────────────────
  // Each sub-score is 0-100 and represents one dimension of technical fit
  type CircuitMatchAssessment = {
    score: number;
    reasons: string[];
  };

  const techCircuitMatch = (
    requiredCircuit: 'open-vented' | 'sealed' | 'any',
    optionType: 'boiler' | 'heat-pump' = 'boiler',
  ): CircuitMatchAssessment => {
    let score = 85;
    const reasons: string[] = [];

    if (!circuitTypeKnown) {
      score = requiredCircuit === 'any' ? 75 : 70;
      reasons.push('circuit type unknown: neutral baseline used');
    } else if (requiredCircuit === 'any') {
      reasons.push('no strict CH pressure regime required for this option');
    } else if (sd.circuitType === requiredCircuit) {
      score += 10;
      reasons.push(`current ${sd.circuitType} CH circuit matches option requirement`);
    } else if (requiredCircuit === 'sealed' && sd.circuitType === 'open-vented') {
      score -= optionType === 'heat-pump' ? 20 : 12;
      reasons.push('open-vented to sealed conversion needed for proposed option');
    } else if (requiredCircuit === 'open-vented' && sd.circuitType === 'sealed') {
      score -= 8;
      reasons.push('sealed to open-vented conversion required for this configuration');
    }

    if (sd.controlTopology === 'y-plan') {
      if (highHeatLoss || optionType === 'heat-pump') {
        score -= optionType === 'heat-pump' ? 12 : 8;
        reasons.push('Y-plan limits zoning/circulation headroom under higher-load design intent');
      } else {
        reasons.push('Y-plan accepted for boiler systems at normal heat loss');
      }
    } else if (sd.controlTopology === 's-plan' || sd.controlTopology === 's-plan-plus') {
      score += 3;
      reasons.push('S-plan topology supports stronger zoning and circulation control');
    }

    if (requiredCircuit === 'open-vented' && optionType === 'boiler' && sd.circuitType === 'open-vented') {
      reasons.push('open-vented CH is treated as valid for gas boiler circulation fit (no default penalty)');
    }

    score = Math.max(20, Math.min(98, score));
    return { score, reasons };
  };

  const techCircuitMatchForOptionId = (optionId: string): CircuitMatchAssessment => {
    if (optionId.startsWith('regular-')) return techCircuitMatch('open-vented', 'boiler');
    if (optionId.startsWith('system-')) return techCircuitMatch('sealed', 'boiler');
    if (optionId.startsWith('ashp') || optionId.startsWith('gshp')) return techCircuitMatch('any', 'heat-pump');
    return techCircuitMatch('any', 'boiler');
  };

  const techMainsAdequacy = (requirement: 'unvented' | 'combi' | 'none'): number => {
    if (requirement === 'none') return 90; // no mains dependency
    if (!coldMainsAssessed) return 30;
    if (requirement === 'unvented') return mainsSupportsUnvented ? 95 : 20;
    if (requirement === 'combi') return mainsSupportsCombi ? 90 : 15;
    return 50;
  };

  const techDhwDelivery = (type: 'vented' | 'unvented' | 'combi' | 'storage-combi'): number => {
    if (type === 'unvented') return mainsSupportsUnvented ? 95 : coldMainsAssessed ? 20 : 40;
    if (type === 'vented') return 55; // gravity/pump = lower performance
    if (type === 'combi') return mainsSupportsCombi ? 70 : 30;
    if (type === 'storage-combi') return mainsSupportsCombi ? 75 : 30;
    return 50;
  };

  const techEmitterCompat = (requiresLowTemp: boolean): number => {
    if (!requiresLowTemp) return 85; // conventional temps, most emitters fine
    if (lowTempReady) return 95;
    if (em.lowTempCapability === 'unknown') return 45;
    return 25; // confirmed not low-temp capable
  };

  const techHeatLossCompat = (hpSensitive: boolean): number => {
    if (!hpSensitive) return 80; // boilers less affected
    if (hd.heatLossBand === 'unknown') return 50;
    if (highHeatLoss) return 25;
    return 85;
  };

  const techStandingLoss = (optionId: string): number => standingLossScore(
    buildEvaluationContext(optionId, hd, sd, wd, em, isLikeForLike(optionId)),
    wd,
  );

  // ── Future plan sub-score helpers ──────────────────────────────────
  const futureHeatPumpReady = (isHP: boolean, isMixergy: boolean): number => {
    if (isHP) return 95;
    if (isMixergy) return 80; // Mixergy cylinders are HP-compatible
    return 40; // standard boiler system
  };

  const futureSolarIntegration = (hasCylinder: boolean, isMixergy: boolean): number => {
    if (isMixergy) return 90; // best for solar integration
    if (hasCylinder) return 70; // can add solar coil
    return 20; // combi — no cylinder for solar
  };

  const futureCapacityGrowth = (type: 'combi' | 'cylinder' | 'hp'): number => {
    if (type === 'hp') return 85;
    if (type === 'cylinder') return 80; // can upsize cylinder
    return 40; // combi limited
  };

  const futureDhwGrowth = (type: 'combi' | 'vented' | 'unvented'): number => {
    if (type === 'unvented') return 90;
    if (type === 'vented') return 75;
    return 35; // combi — flow-rate limited
  };

  // ── Priority sub-score helpers ──────────────────────────────────
  // Each returns 0-100 for how well a given option profile matches a priority key.
  // Option profile is described by: family ('regular'|'system'|'combi'|'hp'),
  // water type, and boolean flags derived from survey data.

  type OptionProfile = {
    family: 'regular' | 'system' | 'combi' | 'storage-combi' | 'hp';
    water: 'vented' | 'unvented' | 'combi' | 'none';
    isMixergy: boolean;
    isLikeForLike: boolean;
  };

  const optionIdForProfile = (profile: OptionProfile): string => {
    if (profile.family === 'hp') return 'ashp-unvented';
    if (profile.family === 'combi') return 'combi';
    if (profile.family === 'storage-combi') return 'storage-combi';
    return `${profile.family}-${profile.isMixergy ? 'mixergy-' : ''}${profile.water === 'unvented' ? 'unvented' : 'open-vented'}`;
  };

  const scorePriorityForOption = (key: string, profile: OptionProfile): number | null => {
    switch (key) {
      case 'low_upfront_cost':
        if (profile.family === 'hp') return 30;
        if (profile.isLikeForLike) return 95;
        if (profile.family === 'combi' || profile.family === 'storage-combi') return 70;
        return profile.isMixergy ? 70 : 85; // Mixergy adds cost

      case 'low_running_cost':
        {
          const optionId = optionIdForProfile(profile);
          const ctx = buildEvaluationContext(optionId, hd, sd, wd, em, profile.isLikeForLike);
          return runningCostPriorityScore(ctx);
        }

      case 'reliability_simplicity':
        if (profile.family === 'combi') return 90;
        if (profile.family === 'storage-combi') return 80;
        if (profile.family === 'regular' || profile.family === 'system') return 80;
        return 55; // hp more complex

      case 'hot_water_performance':
        {
          const occupantCount = Number.parseInt(wd.occupants, 10);
          const highConcurrency = Number.isFinite(occupantCount) && occupantCount >= 4;
          if (profile.water === 'combi' && mainsSupportsCombi) return highConcurrency ? 60 : 75;
        }
        if (profile.water === 'unvented' && mainsSupportsUnvented) return 95;
        if (profile.water === 'unvented') return coldMainsAssessed ? 30 : 50;
        if (profile.water === 'combi') return coldMainsAssessed ? 30 : 45;
        if (profile.isMixergy) return 65;
        return 55; // vented

      case 'quiet_operation':
        if (profile.family === 'hp') return 50;
        return 85;

      case 'future_proofing':
        return futureProofingScore(buildEvaluationContext(optionIdForProfile(profile), hd, sd, wd, em, profile.isLikeForLike));

      case 'minimal_disruption': {
        const ds = disruptionScore(
          profile.family === 'hp' ? 'ashp-unvented'
            : profile.family === 'storage-combi' ? 'storage-combi'
              : `${profile.family}-${profile.isMixergy ? 'mixergy-' : ''}${profile.water === 'combi' ? 'combi' : profile.water === 'unvented' ? 'unvented' : 'open-vented'}`,
        );
        return ds;
      }

      case 'smart_controls':
        if (profile.family === 'hp') return 85;
        if (profile.isMixergy) return 80;
        return 55;

      case 'environmental_impact':
        if (profile.family === 'hp') return 95;
        return 50;

      case 'comfort_even_heat':
        if (profile.family === 'hp') return 85;
        if (profile.isMixergy) return 75;
        return 70;

      default:
        return 50;
    }
  };

  // Compute priorityScores + weighted priorityFit for a given option profile
  const computePriorityBreakdown = (profile: OptionProfile): { priorityScores: Record<string, number>; priorityFit: number } => {
    if (priorities.length === 0) return { priorityScores: {}, priorityFit: 100 };

    const RANK_WEIGHTS: Record<number, number> = { 1: 1.0, 2: 0.7, 3: 0.4 };
    const priorityScores: Record<string, number> = {};
    let weightedSum = 0;
    let totalWeight = 0;

    for (const p of priorities) {
      const score = scorePriorityForOption(p.key, profile);
      if (score === null) {
        continue;
      }
      priorityScores[p.key] = score;
      const w = RANK_WEIGHTS[p.rank] ?? 0.3;
      weightedSum += w * score;
      totalWeight += w;
    }

    return {
      priorityScores,
      priorityFit: totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 100,
    };
  };

  // ── Shared helpers for unvented suitability ────────────────────────
  const unventedSuitability = !coldMainsAssessed ? 'possible-compromised' as const
    : !mainsSupportsUnvented ? 'not-suitable' as const
      : 'highest-standard' as const;
  const unventedFeasibility = !coldMainsAssessed ? 0.7 : !mainsSupportsUnvented ? 0.4 : 1.0;
  const unventedMainsBullet = !coldMainsAssessed
    ? 'Cold mains not assessed — unvented requires measured mains capacity'
    : !mainsSupportsUnvented
      ? 'Cold mains below unvented minimum (need >=1.5 bar, >=15 L/min)'
      : 'Mains-pressure stored hot water — confirmed by measurement';
  const unventedMainsReason = !coldMainsAssessed
    ? 'Cold mains not yet assessed — score provisional'
    : !mainsSupportsUnvented
      ? 'Mains pressure/flow insufficient for unvented operation'
      : 'Mains capacity confirmed adequate for unvented cylinder';

  // ── Pre-compute priority breakdowns per option profile ───────────
  const normaliseId = (id: string) => id.replace('mixergy-', '').replace('storage-', '');
  const isLikeForLike = (optId: string) => currentCat !== 'unknown' && normaliseId(optId) === normaliseId(currentCat);

  const pRegOpenVented       = computePriorityBreakdown({ family: 'regular', water: 'vented',   isMixergy: false, isLikeForLike: isLikeForLike('regular-open-vented') });
  const pRegMixergyVented    = computePriorityBreakdown({ family: 'regular', water: 'vented',   isMixergy: true,  isLikeForLike: isLikeForLike('regular-mixergy-open-vented') });
  const pRegUnvented         = computePriorityBreakdown({ family: 'regular', water: 'unvented', isMixergy: false, isLikeForLike: isLikeForLike('regular-unvented') });
  const pRegMixergyUnvented  = computePriorityBreakdown({ family: 'regular', water: 'unvented', isMixergy: true,  isLikeForLike: isLikeForLike('regular-mixergy-unvented') });
  const pSysOpenVented       = computePriorityBreakdown({ family: 'system',  water: 'vented',   isMixergy: false, isLikeForLike: isLikeForLike('system-open-vented') });
  const pSysMixergyVented    = computePriorityBreakdown({ family: 'system',  water: 'vented',   isMixergy: true,  isLikeForLike: isLikeForLike('system-mixergy-open-vented') });
  const pSysUnvented         = computePriorityBreakdown({ family: 'system',  water: 'unvented', isMixergy: false, isLikeForLike: isLikeForLike('system-unvented') });
  const pSysMixergyUnvented  = computePriorityBreakdown({ family: 'system',  water: 'unvented', isMixergy: true,  isLikeForLike: isLikeForLike('system-mixergy-unvented') });
  const pCombi               = computePriorityBreakdown({ family: 'combi',         water: 'combi', isMixergy: false, isLikeForLike: isLikeForLike('combi') });
  const pStorageCombi        = computePriorityBreakdown({ family: 'storage-combi', water: 'combi', isMixergy: false, isLikeForLike: isLikeForLike('storage-combi') });
  const pAshp                = computePriorityBreakdown({ family: 'hp', water: 'unvented', isMixergy: false, isLikeForLike: isLikeForLike('ashp-unvented') });
  const pGshp                = computePriorityBreakdown({ family: 'hp', water: 'unvented', isMixergy: false, isLikeForLike: isLikeForLike('gshp-unvented') });

  const rawOptions: OptionCardData[] = [
    // ═══════════════════════════════════════════════════════════════════
    // REGULAR BOILER OPTIONS (open-vented CH circuit, F&E tank)
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'regular-open-vented',
      label: 'Regular - open vented',
      suitability: 'possible-compromised',
      suitabilityScore: scaleScore(circuitTypeKnown && sd.circuitType === 'open-vented' ? 68 : 60),
      confidence,
      becauseBullets: [
        'Regular boiler with traditional open-vented hot water cylinder',
        'Gravity-fed or pumped DHW — no mains pressure dependency',
        circuitTypeKnown && sd.circuitType === 'open-vented'
          ? 'Existing open-vented circuit confirmed'
          : 'Compatible with open-vented heating circuits',
      ],
      assumptions: circuitTypeKnown && sd.circuitType === 'open-vented'
        ? []
        : ['F&E tank and open-vented architecture remain serviceable'],
      wouldNeedToChange: sd.circuitType === 'sealed' ? ['Reconfigure to open-vented primary circuit'] : [],
      wontSolve: ['Mains-pressure hot water without additional upgrades', 'Low pressure at outlets without pumps'],
      scoreBreakdown: {
        technicalFit: circuitTypeKnown && sd.circuitType === 'open-vented' ? 72 : 60,
        priorityFit: pRegOpenVented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('regular-open-vented', hd, sd, wd, em, isLikeForLike('regular-open-vented'))),
        feasibility: 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('open-vented', 'boiler').score,
            mains_adequacy: techMainsAdequacy('none'),
            dhw_delivery: techDhwDelivery('vented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('regular-open-vented'),
          },
          priorityScores: pRegOpenVented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('vented'),
          },
          reasons: [
            'Traditional open-vented cylinder — reliable, low maintenance',
            'Cylinder standing loss (~1.8 kWh/day) is the trade-off for stored hot water with gravity-fed delivery',
            'Lower hot water delivery pressure than unvented alternatives',
            'No mains pressure requirement for DHW',
            ...(sd.circuitType === 'sealed' ? ['Would need circuit conversion from sealed to open-vented'] : []),
          ],
        },
      },
    },
    {
      id: 'regular-mixergy-open-vented',
      label: 'Regular - Mixergy open vented',
      suitability: 'possible-compromised',
      suitabilityScore: scaleScore(circuitTypeKnown && sd.circuitType === 'open-vented' ? 76 : 68),
      confidence,
      becauseBullets: [
        'Regular boiler with Mixergy smart open-vented cylinder',
        'Up to 70% reduction in standing losses vs standard vented cylinder (~0.8 kWh/day)',
        'No mains pressure dependency — works with any cold mains',
      ],
      assumptions: [
        ...(circuitTypeKnown && sd.circuitType === 'open-vented' ? [] : ['F&E tank and open-vented architecture remain serviceable']),
        'Space for Mixergy cylinder',
      ],
      wouldNeedToChange: sd.circuitType === 'sealed' ? ['Reconfigure to open-vented primary circuit'] : [],
      wontSolve: ['Mains-pressure hot water — still gravity/pump-fed delivery'],
      scoreBreakdown: {
        technicalFit: circuitTypeKnown && sd.circuitType === 'open-vented' ? 80 : 68,
        priorityFit: pRegMixergyVented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('regular-mixergy-open-vented', hd, sd, wd, em, isLikeForLike('regular-mixergy-open-vented'))),
        feasibility: 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('open-vented', 'boiler').score,
            mains_adequacy: techMainsAdequacy('none'),
            dhw_delivery: techDhwDelivery('vented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('regular-mixergy-open-vented'),
          },
          priorityScores: pRegMixergyVented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, true),
            solar_integration: futureSolarIntegration(true, true),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('vented'),
          },
          reasons: [
            'Smart stratification — heats only the water you need',
            'Standing losses reduced by ~70% vs standard vented (~0.8 kWh/day)',
            'Future-ready: compatible with solar thermal and heat pump integration',
            'Lower delivery pressure than unvented alternatives',
          ],
        },
      },
    },
    {
      id: 'regular-unvented',
      label: 'Regular - unvented',
      suitability: unventedSuitability,
      suitabilityScore: scaleScore(!coldMainsAssessed ? 48 : !mainsSupportsUnvented ? 28 : 82),
      confidence,
      becauseBullets: [
        unventedMainsBullet,
        'Regular boiler paired with mains-fed unvented cylinder',
        'Keeps regular-boiler platform while delivering stored mains-pressure hot water',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : ['Space for unvented cylinder'],
      wouldNeedToChange: [
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'G3-qualified installation required',
      ],
      wontSolve: ['Heat loss issues without emitter/fabric improvements'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 48 : !mainsSupportsUnvented ? 28 : 84,
        priorityFit: pRegUnvented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('regular-unvented', hd, sd, wd, em, isLikeForLike('regular-unvented'))),
        feasibility: unventedFeasibility,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('open-vented', 'boiler').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('regular-unvented'),
          },
          priorityScores: pRegUnvented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            unventedMainsReason,
            'Mains-pressure delivery at all outlets (no pumps needed)',
            'Cylinder standing loss (~1.2 kWh/day) is the pre-payment for high-flow multi-outlet capability',
            ...(unventedFeasibility < 1 ? [`Feasibility reduced: ${(unventedFeasibility * 100).toFixed(0)}%`] : []),
          ],
        },
      },
    },
    {
      id: 'regular-mixergy-unvented',
      label: 'Regular - Mixergy unvented',
      suitability: unventedSuitability,
      suitabilityScore: scaleScore(!coldMainsAssessed ? 52 : !mainsSupportsUnvented ? 30 : 88),
      confidence,
      becauseBullets: [
        unventedMainsBullet,
        'Regular boiler with Mixergy smart unvented cylinder',
        'Up to 70% reduction in standing losses vs standard unvented (~0.36 kWh/day)',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : ['Space for Mixergy unvented cylinder'],
      wouldNeedToChange: [
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'G3-qualified installation required',
      ],
      wontSolve: ['Heat loss issues without emitter/fabric improvements'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 52 : !mainsSupportsUnvented ? 30 : 90,
        priorityFit: pRegMixergyUnvented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('regular-mixergy-unvented', hd, sd, wd, em, isLikeForLike('regular-mixergy-unvented'))),
        feasibility: unventedFeasibility,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('open-vented', 'boiler').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('regular-mixergy-unvented'),
          },
          priorityScores: pRegMixergyUnvented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, true),
            solar_integration: futureSolarIntegration(true, true),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            unventedMainsReason,
            'Smart stratification — heats only the volume needed',
            'Standing losses reduced to ~0.36 kWh/day (vs 1.2 kWh/day standard unvented)',
            'Future-ready: solar thermal, heat pump integration, load shaping',
            ...(unventedFeasibility < 1 ? [`Feasibility reduced: ${(unventedFeasibility * 100).toFixed(0)}%`] : []),
          ],
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════
    // SYSTEM BOILER OPTIONS (sealed CH circuit, expansion vessel)
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'system-open-vented',
      label: 'System - open vented',
      suitability: 'possible-compromised',
      suitabilityScore: scaleScore(circuitTypeKnown && sd.circuitType === 'sealed' ? 65 : 58),
      confidence,
      becauseBullets: [
        'System boiler (sealed CH) with open-vented hot water cylinder',
        'Sealed heating circuit — modern, less corrosion risk',
        'DHW still gravity/pump-fed from vented cylinder',
      ],
      assumptions: circuitTypeKnown && sd.circuitType === 'sealed'
        ? ['Space for vented cylinder and cold water storage tank']
        : ['Sealed CH circuit or conversion feasible', 'Space for vented cylinder'],
      wouldNeedToChange: sd.circuitType === 'open-vented' ? ['Sealed conversion for CH circuit'] : [],
      wontSolve: ['Mains-pressure hot water — vented cylinder limits delivery pressure'],
      scoreBreakdown: {
        technicalFit: circuitTypeKnown && sd.circuitType === 'sealed' ? 70 : 58,
        priorityFit: pSysOpenVented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('system-open-vented', hd, sd, wd, em, isLikeForLike('system-open-vented'))),
        feasibility: 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('sealed', 'boiler').score,
            mains_adequacy: techMainsAdequacy('none'),
            dhw_delivery: techDhwDelivery('vented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('system-open-vented'),
          },
          priorityScores: pSysOpenVented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('vented'),
          },
          reasons: [
            'Sealed CH circuit reduces corrosion and maintenance',
            'Cylinder standing loss (~1.8 kWh/day) is the trade-off for reliable stored hot water',
            'Vented DHW — lower delivery pressure than unvented',
            'Requires cold water storage tank in loft',
            ...(sd.circuitType === 'open-vented' ? ['CH circuit would need sealed conversion'] : []),
          ],
        },
      },
    },
    {
      id: 'system-mixergy-open-vented',
      label: 'System - Mixergy open vented',
      suitability: 'possible-compromised',
      suitabilityScore: scaleScore(circuitTypeKnown && sd.circuitType === 'sealed' ? 73 : 65),
      confidence,
      becauseBullets: [
        'System boiler (sealed CH) with Mixergy smart open-vented cylinder',
        'Up to 70% reduction in standing losses vs standard vented (~0.8 kWh/day)',
        'Sealed heating circuit — modern, less corrosion risk',
      ],
      assumptions: [
        ...(circuitTypeKnown && sd.circuitType === 'sealed' ? [] : ['Sealed CH circuit or conversion feasible']),
        'Space for Mixergy cylinder',
      ],
      wouldNeedToChange: sd.circuitType === 'open-vented' ? ['Sealed conversion for CH circuit'] : [],
      wontSolve: ['Mains-pressure hot water — still gravity/pump-fed delivery'],
      scoreBreakdown: {
        technicalFit: circuitTypeKnown && sd.circuitType === 'sealed' ? 78 : 65,
        priorityFit: pSysMixergyVented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('system-mixergy-open-vented', hd, sd, wd, em, isLikeForLike('system-mixergy-open-vented'))),
        feasibility: 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('sealed', 'boiler').score,
            mains_adequacy: techMainsAdequacy('none'),
            dhw_delivery: techDhwDelivery('vented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('system-mixergy-open-vented'),
          },
          priorityScores: pSysMixergyVented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, true),
            solar_integration: futureSolarIntegration(true, true),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('vented'),
          },
          reasons: [
            'Sealed CH with smart Mixergy stratification',
            'Standing losses reduced by ~70% vs standard vented (~0.8 kWh/day)',
            'Future-ready: compatible with solar thermal and heat pump integration',
            ...(sd.circuitType === 'open-vented' ? ['CH circuit would need sealed conversion'] : []),
          ],
        },
      },
    },
    {
      id: 'system-unvented',
      label: 'System - unvented',
      suitability: unventedSuitability,
      suitabilityScore: scaleScore(!coldMainsAssessed ? 50 : !mainsSupportsUnvented ? 30 : 88),
      confidence,
      becauseBullets: [
        unventedMainsBullet,
        'System boiler (sealed CH) with unvented cylinder — fully pressurised system',
        'Supports simultaneous draws, S-plan zoning',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : ['Space for unvented cylinder'],
      wouldNeedToChange: [
        ...(sd.circuitType === 'open-vented' ? ['Sealed conversion for CH circuit'] : []),
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'G3-qualified installation required',
      ],
      wontSolve: ['Fabric heat losses — separate issue'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 50 : !mainsSupportsUnvented ? 30 : 90,
        priorityFit: pSysUnvented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('system-unvented', hd, sd, wd, em, isLikeForLike('system-unvented'))),
        feasibility: unventedFeasibility,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('sealed', 'boiler').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('system-unvented'),
          },
          priorityScores: pSysUnvented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            unventedMainsReason,
            'Cylinder standing loss is the pre-payment for high flow rates and simultaneous outlet capacity',
            'Fully pressurised system — sealed CH + mains-pressure DHW',
            'No loft tanks required',
            ...(unventedFeasibility < 1 ? [`Feasibility reduced: ${(unventedFeasibility * 100).toFixed(0)}%`] : []),
          ],
        },
      },
    },
    {
      id: 'system-mixergy-unvented',
      label: 'System - Mixergy unvented',
      suitability: unventedSuitability,
      suitabilityScore: scaleScore(!coldMainsAssessed ? 55 : !mainsSupportsUnvented ? 32 : 93),
      confidence,
      becauseBullets: [
        unventedMainsBullet,
        'System boiler with Mixergy smart unvented — strong fit where mains pressure and smart storage strategy are both priorities',
        'Up to 70% reduction in standing losses vs standard unvented (~0.36 kWh/day)',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : ['Space for Mixergy unvented cylinder'],
      wouldNeedToChange: [
        ...(sd.circuitType === 'open-vented' ? ['Sealed conversion for CH circuit'] : []),
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'G3-qualified installation required',
      ],
      wontSolve: ['Fabric heat losses — separate issue'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 55 : !mainsSupportsUnvented ? 32 : 95,
        priorityFit: pSysMixergyUnvented.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('system-mixergy-unvented', hd, sd, wd, em, isLikeForLike('system-mixergy-unvented'))),
        feasibility: unventedFeasibility,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('sealed', 'boiler').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('system-mixergy-unvented'),
          },
          priorityScores: pSysMixergyUnvented.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, true),
            solar_integration: futureSolarIntegration(true, true),
            capacity_growth: futureCapacityGrowth('cylinder'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            unventedMainsReason,
            'Smart stratification — heats only the volume needed',
            'Standing losses reduced to ~0.36 kWh/day (vs 1.2 kWh/day standard unvented)',
            'Future-ready: solar thermal, heat pump integration, load shaping',
            ...(unventedFeasibility < 1 ? [`Feasibility reduced: ${(unventedFeasibility * 100).toFixed(0)}%`] : []),
          ],
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════
    // COMBI OPTIONS
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'combi',
      label: 'Combi',
      suitability: !coldMainsAssessed ? 'not-suitable'
        : hasCylinderConflict ? 'not-suitable'
          : !mainsSupportsCombi ? 'not-suitable'
            : 'possible-compromised',
      suitabilityScore: scaleScore(!coldMainsAssessed ? 0
        : hasCylinderConflict ? 15
          : !mainsSupportsCombi ? 25
            : 62),
      confidence,
      becauseBullets: [
        !coldMainsAssessed ? 'Cannot evaluate — cold mains not assessed'
          : hasCylinderConflict ? 'Bronze secondary return rules out combi'
            : !mainsSupportsCombi ? 'Cold mains flow/pressure below robust combi threshold'
              : 'Cold mains supports on-demand delivery (measured)',
        'No cylinder, but expect delivery lag and some draw-off waste from pipe runs',
        dynamicPairAssessed ? 'Dynamic pair measured under load' : 'Dynamic pair not measured (confidence reduced)',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : [
            ...(!dynamicPairAssessed ? ['Static measurement may not reflect peak-use conditions'] : []),
          ],
      wouldNeedToChange: hasCylinderConflict ? ['Remove bronze secondary return'] : [],
      wontSolve: ['High simultaneous demand', 'Long pipe runs to distant outlets'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 0 : hasCylinderConflict ? 15 : !mainsSupportsCombi ? 25 : 65,
        priorityFit: pCombi.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('combi', hd, sd, wd, em, isLikeForLike('combi'))),
        feasibility: !coldMainsAssessed ? 0.0 : hasCylinderConflict ? 0.2 : !mainsSupportsCombi ? 0.4 : 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('any', 'boiler').score,
            mains_adequacy: techMainsAdequacy('combi'),
            dhw_delivery: techDhwDelivery('combi'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('combi'),
          },
          priorityScores: pCombi.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(false, false),
            capacity_growth: futureCapacityGrowth('combi'),
            dhw_growth: futureDhwGrowth('combi'),
          },
          reasons: [
            'No cylinder standing loss, but combi still has tap lag and draw-off waste/engine overhead',
            'Limited by mains flow rate for simultaneous draws',
            'Compact — no cylinder space required',
            ...(hasCylinderConflict ? ['Bronze secondary return incompatible with combi'] : []),
          ],
          killSwitchReasons: !coldMainsAssessed
            ? ['Cold mains not assessed — cannot validate flow rate and pressure requirements']
            : hasCylinderConflict
              ? ['Bronze secondary return circuit incompatible with combi boiler operation']
              : !mainsSupportsCombi
                ? [
                    ...(mainsFlow && mainsFlow < COMBI_MIN_FLOW_RATE ? [`Mains flow rate (${mainsFlow.toFixed(1)} L/min) below combi minimum (${COMBI_MIN_FLOW_RATE} L/min)`] : []),
                    ...(mainsStaticPressure && mainsStaticPressure < COMBI_MIN_PRESSURE ? [`Mains pressure (${mainsStaticPressure.toFixed(1)} bar) below combi minimum (${COMBI_MIN_PRESSURE} bar)`] : []),
                    'Simultaneous tap demand would exceed available flow rate',
                  ].filter(Boolean)
                : undefined,
          penalties: !mainsSupportsCombi && coldMainsAssessed && mainsFlow && mainsFlow < COMBI_MIN_FLOW_RATE ? [
            {
              dimension: 'Mains adequacy',
              penalty: -40,
              reason: `Flow rate ${mainsFlow.toFixed(1)} L/min < ${COMBI_MIN_FLOW_RATE} L/min required for robust combi operation`,
            },
          ] : [],
        },
      },
    },
    {
      id: 'storage-combi',
      label: 'Storage combi',
      suitability: !coldMainsAssessed ? 'not-suitable'
        : !mainsSupportsCombi ? 'not-suitable'
          : 'possible-compromised',
      suitabilityScore: scaleScore(!coldMainsAssessed ? 0
        : !mainsSupportsCombi ? 25
          : 58),
      confidence,
      becauseBullets: [
        !coldMainsAssessed ? 'Cannot evaluate — cold mains not assessed'
          : !mainsSupportsCombi ? 'Cold mains flow/pressure below robust combi threshold'
            : 'Combi with small internal store for improved first-draw performance',
        'Better simultaneous draw performance than standard combi',
        'Larger unit footprint than standard combi',
      ],
      assumptions: !coldMainsAssessed
        ? ['Cold mains must be assessed first']
        : ['Space for larger combi unit'],
      wouldNeedToChange: [],
      wontSolve: ['Very high simultaneous demand', 'Long pipe runs to distant outlets'],
      scoreBreakdown: {
        technicalFit: !coldMainsAssessed ? 0 : !mainsSupportsCombi ? 25 : 60,
        priorityFit: pStorageCombi.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('storage-combi', hd, sd, wd, em, isLikeForLike('storage-combi'))),
        feasibility: !coldMainsAssessed ? 0.0 : !mainsSupportsCombi ? 0.4 : 1.0,
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('any', 'boiler').score,
            mains_adequacy: techMainsAdequacy('combi'),
            dhw_delivery: techDhwDelivery('storage-combi'),
            emitter_compat: techEmitterCompat(false),
            standing_loss: techStandingLoss('combi'),
          },
          priorityScores: pStorageCombi.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(false, false),
            solar_integration: futureSolarIntegration(false, false),
            capacity_growth: futureCapacityGrowth('combi'),
            dhw_growth: futureDhwGrowth('combi'),
          },
          reasons: [
            'Small internal store improves first-draw and short-burst performance',
            'Still limited by mains for sustained simultaneous draws',
            'Larger than standard combi — check available space',
            'No external cylinder required',
          ],
          killSwitchReasons: !coldMainsAssessed
            ? ['Cold mains not assessed — cannot validate flow rate and pressure requirements']
            : !mainsSupportsCombi
              ? [
                  ...(mainsFlow && mainsFlow < COMBI_MIN_FLOW_RATE ? [`Mains flow rate (${mainsFlow.toFixed(1)} L/min) below combi minimum (${COMBI_MIN_FLOW_RATE} L/min)`] : []),
                  ...(mainsStaticPressure && mainsStaticPressure < COMBI_MIN_PRESSURE ? [`Mains pressure (${mainsStaticPressure.toFixed(1)} bar) below combi minimum (${COMBI_MIN_PRESSURE} bar)`] : []),
                  'Internal storage cannot compensate for sustained simultaneous demand exceeding mains capacity',
                ].filter(Boolean)
              : undefined,
          penalties: !mainsSupportsCombi && coldMainsAssessed && mainsFlow && mainsFlow < COMBI_MIN_FLOW_RATE ? [
            {
              dimension: 'Mains adequacy',
              penalty: -35,
              reason: `Flow rate ${mainsFlow.toFixed(1)} L/min insufficient for sustained multi-outlet use`,
            },
          ] : [],
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════
    // HEAT PUMP OPTIONS
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'ashp-unvented',
      label: 'Air source heat pump - unvented',
      suitability: lowTempReady && !highHeatLoss && mainsSupportsUnvented ? 'highest-standard'
        : !coldMainsAssessed ? 'possible-compromised'
          : !mainsSupportsUnvented ? 'not-suitable'
            : 'possible-compromised',
      suitabilityScore: scaleScore(
        lowTempReady && !highHeatLoss && mainsSupportsUnvented ? 85
          : !coldMainsAssessed ? 35
            : !mainsSupportsUnvented ? 20
              : 50,
      ),
      confidence: lowTempReady ? 'medium' : 'low',
      becauseBullets: [
        lowTempReady ? 'Emitters confirmed suitable for low-temp operation' : 'Low-temp suitability unconfirmed',
        highHeatLoss ? 'High heat loss — fabric improvements needed first' : 'Heat loss within ASHP operating range',
        unventedMainsBullet,
      ],
      assumptions: [
        'Outdoor unit space available',
        'Electrical supply adequate (single/three phase)',
        ...(em.lowTempCapability === 'unknown' ? ['Emitter low-temp suitability not assessed'] : []),
        ...(hd.heatLossBand === 'unknown' ? ['Heat loss band not assessed — using estimate'] : []),
      ],
      wouldNeedToChange: [
        ...(!lowTempReady ? ['Confirm or upgrade emitters for low-temp operation'] : []),
        ...(highHeatLoss ? ['Fabric improvements to reduce heat loss'] : []),
        ...(em.primarySizeBand === '15mm' ? ['Upsize primaries from 15 mm'] : []),
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'G3-qualified installation required for unvented cylinder',
      ],
      wontSolve: ['Rapid recovery — heat pump heats slowly by design', 'Very high DHW demand peaks'],
      scoreBreakdown: {
        technicalFit: lowTempReady && !highHeatLoss ? 85 : 45,
        priorityFit: pAshp.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('ashp-unvented', hd, sd, wd, em, isLikeForLike('ashp-unvented'))),
        feasibility: (!coldMainsAssessed ? 0.5 : !mainsSupportsUnvented ? 0.3 : 1.0) * (lowTempReady ? 1.0 : 0.6),
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('any', 'heat-pump').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(true),
            heat_loss_compat: techHeatLossCompat(true),
            standing_loss: techStandingLoss('ashp-unvented'),
          },
          priorityScores: pAshp.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(true, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('hp'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            'Renewable heating — lowest carbon option',
            lowTempReady ? 'Emitters suitable for low-flow temperatures' : 'Emitters may need upgrading for low-temp',
            'Unvented cylinder provides mains-pressure DHW',
            highHeatLoss ? 'High heat loss reduces COP — fabric first' : 'Heat loss acceptable for ASHP',
            'Eligible for BUS / RHI grants',
          ],
        },
      },
    },
    {
      id: 'gshp-unvented',
      label: 'Ground source heat pump - unvented',
      suitability: lowTempReady && !highHeatLoss && mainsSupportsUnvented ? 'highest-standard'
        : !coldMainsAssessed ? 'possible-compromised'
          : !mainsSupportsUnvented ? 'not-suitable'
            : 'possible-compromised',
      suitabilityScore: scaleScore(
        lowTempReady && !highHeatLoss && mainsSupportsUnvented ? 82
          : !coldMainsAssessed ? 30
            : !mainsSupportsUnvented ? 18
              : 45,
      ),
      confidence: lowTempReady ? 'medium' : 'low',
      becauseBullets: [
        lowTempReady ? 'Emitters confirmed suitable for low-temp operation' : 'Low-temp suitability unconfirmed',
        'Higher COP than ASHP — more consistent ground temperatures',
        'Requires ground loop or borehole — significant installation',
      ],
      assumptions: [
        'Sufficient garden/land area for ground loop or borehole',
        'Electrical supply adequate',
        ...(em.lowTempCapability === 'unknown' ? ['Emitter low-temp suitability not assessed'] : []),
        ...(hd.heatLossBand === 'unknown' ? ['Heat loss band not assessed — using estimate'] : []),
      ],
      wouldNeedToChange: [
        ...(!lowTempReady ? ['Confirm or upgrade emitters for low-temp operation'] : []),
        ...(highHeatLoss ? ['Fabric improvements to reduce heat loss'] : []),
        ...(em.primarySizeBand === '15mm' ? ['Upsize primaries from 15 mm'] : []),
        ...(!coldMainsAssessed ? ['Measure cold mains pressure and flow'] : []),
        'Ground loop/borehole installation',
        'G3-qualified installation required for unvented cylinder',
      ],
      wontSolve: ['Rapid recovery — heat pump heats slowly by design', 'Properties with no garden or restricted access'],
      scoreBreakdown: {
        technicalFit: lowTempReady && !highHeatLoss ? 82 : 40,
        priorityFit: pGshp.priorityFit,
        futureFit: futureProofingScore(buildEvaluationContext('gshp-unvented', hd, sd, wd, em, isLikeForLike('gshp-unvented'))),
        feasibility: (!coldMainsAssessed ? 0.4 : !mainsSupportsUnvented ? 0.25 : 0.9) * (lowTempReady ? 1.0 : 0.6),
        details: {
          technicalSubScores: {
            circuit_match: techCircuitMatch('any', 'heat-pump').score,
            mains_adequacy: techMainsAdequacy('unvented'),
            dhw_delivery: techDhwDelivery('unvented'),
            emitter_compat: techEmitterCompat(true),
            heat_loss_compat: techHeatLossCompat(true),
            standing_loss: techStandingLoss('gshp-unvented'),
          },
          priorityScores: pGshp.priorityScores,
          futurePlanScores: {
            heat_pump_ready: futureHeatPumpReady(true, false),
            solar_integration: futureSolarIntegration(true, false),
            capacity_growth: futureCapacityGrowth('hp'),
            dhw_growth: futureDhwGrowth('unvented'),
          },
          reasons: [
            'Renewable heating — lowest carbon option with highest COP',
            'Ground temperatures more stable than air — better winter performance',
            'Significant upfront cost and installation disruption',
            highHeatLoss ? 'High heat loss reduces COP — fabric first' : 'Heat loss acceptable for GSHP',
            'Eligible for BUS / RHI grants',
          ],
        },
      },
    },
  ];

  // ═══════════════════════════════════════════════════════════════════
  // POST-PROCESSING: Apply disruption & cylinder-space adjustments
  // ═══════════════════════════════════════════════════════════════════
  const integrationComplexityForOption = (optionId: string): number => {
    const likeForLike = isLikeForLike(optionId);
    if (likeForLike) return 88;

    const proposesSealed = optionId.startsWith('system') || optionId.startsWith('ashp') || optionId.startsWith('gshp');
    const topologyChange = (sd.circuitType === 'open-vented' && proposesSealed) || (sd.circuitType === 'sealed' && optionId.startsWith('regular'));
    const primariesUpgrade = em.primarySizeBand !== '28mm' && (optionId.startsWith('ashp') || optionId.startsWith('gshp'));

    let score = 72;
    if (topologyChange) score -= 18;
    if (primariesUpgrade) score -= 10;
    if (optionId.includes('unvented') && sd.dhwArchitecture !== 'stored-dhw') score -= 10;
    return Math.max(30, score);
  };

  return rawOptions.map(opt => {
    const disruption = disruptionScore(opt.id);
    const isLikeForLikeOption = isLikeForLike(opt.id);
    const evalContext = buildEvaluationContext(opt.id, hd, sd, wd, em, isLikeForLikeOption);
    const cylNeeded = needsCylinder(opt.id);
    const integrationComplexity = integrationComplexityForOption(opt.id);

    // ── DERIVED SCORES (replaces hardcoded numbers) ──────────────
    // Technical fit is now derived from evaluation context, not hardcoded
    const derived = deriveTechnicalFit(
      evalContext, sd, wd,
      coldMainsAssessed, mainsSupportsUnvented, mainsSupportsCombi,
      hasCylinderConflict, highHeatLoss,
    );

    // Cylinder space: hard block if no space and cylinder needed
    let cylinderFeasibility = 1.0;
    const extraAssumptions: string[] = [];
    const extraChanges: string[] = [];
    const extraReasons: string[] = [];

    if (cylNeeded) {
      if (noCylinderSpace) {
        cylinderFeasibility = 0.3;
        extraChanges.push('No cylinder space identified — would need to create space');
        extraReasons.push('No cylinder space: feasibility significantly reduced');
      } else if (cylinderSpaceUnknown) {
        cylinderFeasibility = 0.8;
        extraAssumptions.push('Cylinder space not yet assessed');
        extraReasons.push('Cylinder space not confirmed — score provisional');
      } else if (hasCylinderSpace) {
        extraReasons.push(
          sd.cylinderSpace === 'yes-existing'
            ? 'Cylinder space confirmed (existing location)'
            : 'Cylinder space confirmed (available)',
        );
      }
    }

    if (!evalContext.annualDemandKnown) {
      extraAssumptions.push('Running cost not assessed — annual demand inputs are incomplete');
      extraReasons.push('Running cost priority excluded until demand inputs are complete');
    }

    // Cylinder retention/replacement description
    const cylRetentionDesc = cylinderRetentionDescription(evalContext);
    if (cylRetentionDesc) {
      extraReasons.push(cylRetentionDesc);
    }

    // Standing loss description with provenance
    const standingLossDesc = standingLossDescription(evalContext, wd);
    if (standingLossDesc) {
      extraReasons.push(standingLossDesc);
    }

    // Disruption reason text
    if (isLikeForLikeOption) {
      extraReasons.push('Like-for-like replacement — minimal disruption');
    } else if (disruption >= 80) {
      extraReasons.push('Same boiler family — moderate disruption');
    } else if (disruption >= 55) {
      extraReasons.push('Different boiler type — significant pipework changes likely');
    } else {
      extraReasons.push('Major system change — high disruption expected');
    }

    // Use DERIVED technical fit score instead of hardcoded suitabilityScore
    const derivedSuitabilityScore = scaleScore(derived.score);

    // Blend disruption into the derived score (10% weight)
    const disruptionAdjusted = Math.round(derivedSuitabilityScore * 0.9 + disruption * 0.1);

    // Apply cylinder feasibility on top of existing feasibility
    const existingFeasibility = opt.scoreBreakdown?.feasibility ?? 1.0;
    const combinedFeasibility = existingFeasibility * cylinderFeasibility;

    // Final score with cylinder feasibility applied
    const finalScore = Math.round(disruptionAdjusted * combinedFeasibility);

    // Determine suitability from derived score
    let suitability = opt.suitability;
    if (finalScore >= 70) {
      suitability = 'highest-standard';
    } else if (finalScore >= 30) {
      suitability = 'possible-compromised';
    } else {
      suitability = 'not-suitable';
    }
    // Downgrade suitability if cylinder blocks it
    if (cylNeeded && noCylinderSpace && suitability === 'highest-standard') {
      suitability = 'possible-compromised';
    }

    const hardConstraintReasons = opt.scoreBreakdown?.details?.constraintReasons?.filter((reason) => reason.kind === 'hard') ?? [];
    const derivedComponentWeight = 1 / Math.max(1, Object.keys(derived.components).length);
    const debugPayload = devtoolsEnabled ? {
      context: {
        ...evalContext,
        circuitType: sd.circuitType,
        controlTopology: sd.controlTopology,
        coldMains: {
          mainsFlowLpm: mainsFlow,
          mainsStaticPressureBar: mainsStaticPressure,
          mainsDynamicFlowLpm: mainsDynamicFlow,
          mainsDynamicPressureBar: mainsDynamicPressure,
        },
        standingLoss: {
          value: wd.standingLoss,
          band: wd.standingLossBand,
          retentionMode: evalContext.cylinderRetention,
        },
      },
      constraints: opt.scoreBreakdown?.details?.constraintReasons ?? [],
      components: Object.entries(derived.components).map(([key, value]) => {
        const componentScore = Number.isFinite(value) ? Number(value) : null;
        const componentWeight = Number(derivedComponentWeight.toFixed(3));
        const multiplier = 1;
        const weightedContribution = componentScore == null ? null : Number((componentScore * componentWeight * multiplier).toFixed(2));

        return {
          key,
          inputs: {
            optionId: opt.id,
            family: evalContext.family,
            dhwArchitecture: evalContext.dhwArchitecture,
            annualDemandKnown: evalContext.annualDemandKnown,
          },
          rules: [{ id: `derived.${key}`, result: value }],
          score: componentScore,
          weight: componentWeight,
          multiplier,
          weightedContribution,
        };
      }),
      aggregation: {
        technicalAggregation: {
          formula: 'technicalFit = clamp((Σ component_i) / maxPossible × 100, 0, 100)',
          weightsByComponent: Object.fromEntries(Object.keys(derived.components).map((key) => [key, Number(derivedComponentWeight.toFixed(3))])),
          preGateScore: derived.score,
          gateMultiplier: 1,
          gateReasons: ['no technical gate multiplier applied in derived hub scoring'],
          preClampScore: derived.score,
          postClampScore: derived.score,
          clampReasons: [],
        },
        buckets: [
          { key: 'technicalFit', score: derived.score, weight: 0.55, contribution: Number((derived.score * 0.55).toFixed(2)) },
          { key: 'priorityFit', score: opt.scoreBreakdown?.priorityFit ?? null, weight: 0.30, contribution: opt.scoreBreakdown?.priorityFit != null ? Number((opt.scoreBreakdown.priorityFit * 0.3).toFixed(2)) : null },
          { key: 'futureFit', score: opt.scoreBreakdown?.futureFitAssessed === false ? null : (opt.scoreBreakdown?.futureFit ?? null), weight: 0.15, contribution: opt.scoreBreakdown?.futureFitAssessed === false || opt.scoreBreakdown?.futureFit == null ? null : Number((opt.scoreBreakdown.futureFit * 0.15).toFixed(2)) },
        ],
        overall: {
          score: finalScore,
          formula: 'overall = feasibility × (0.55×technicalFit + 0.30×priorityFit + 0.15×futureFit)',
        },
        clamps: hardConstraintReasons.length > 0 || combinedFeasibility < 1
          ? [{
            id: 'feasibility-clamp',
            before: disruptionAdjusted,
            after: finalScore,
            reason: hardConstraintReasons.length > 0
              ? `hard constraints: ${hardConstraintReasons.map((reason) => reason.id).join(', ')}`
              : `feasibility factor ${combinedFeasibility.toFixed(2)}`,
          }]
          : undefined,
      },
    } : undefined;

    const explanationItems = rankExplanationItems(buildExplanationItems({
      ...opt,
      fitScore: opt.scoreBreakdown ? {
        ...opt.scoreBreakdown,
        breakdown: opt.scoreBreakdown.details,
      } : undefined,
    } as RecommendationOption, {
      reasons: extraReasons,
      assumptions: extraAssumptions,
      changes: extraChanges,
    }));

    const isAshpOption = opt.id.startsWith('ashp');
    const emitterCompatibility = derived.components.emitter_compat ?? 45;
    const architectureLevel: OptionCardData['architectureLevel'] = emitterCompatibility < 60
      ? 'full_redesign'
      : emitterCompatibility < 75
        ? 'moderate_upgrade'
        : 'compatible';
    const retrofitScore = isAshpOption ? Math.round(derived.score) : undefined;
    let redesignedScore = retrofitScore;
    if (isAshpOption && typeof retrofitScore === 'number') {
      redesignedScore = Math.min(100, retrofitScore
        + Math.max(0, 90 - emitterCompatibility) * 0.4
        + ((derived.components.heat_loss_compat ?? 50) >= 70 ? 5 : 0)
        + ((derived.components.mains_adequacy ?? 50) >= 70 ? 3 : 0)
        + ((hd.knownHeatLossKw ?? 99) < 18 ? 5 : 0));
      redesignedScore = Math.round(redesignedScore);
    }

    return {
      ...opt,
      suitability,
      suitabilityScore: finalScore,
      scoreLabel: getScoreLabel(finalScore),
      retrofitScore,
      redesignedScore,
      architectureLevel: isAshpOption ? architectureLevel : undefined,
      redesignRequirements: isAshpOption ? [
        'Radiator upgrades sized for ≤45°C flow',
        'Weather compensation control',
        'Full room-by-room heat loss',
        'Correctly matched cylinder',
        'Hydraulic optimisation where required',
      ] : undefined,
      becauseBullets: explanationItems
        .slice(0, DEFAULT_VISIBLE_EXPLANATION_ITEMS)
        .map((item) => item.text),
      assumptions: [...opt.assumptions, ...extraAssumptions],
      wouldNeedToChange: [...opt.wouldNeedToChange, ...extraChanges],
      scoreBreakdown: opt.scoreBreakdown ? {
        ...opt.scoreBreakdown,
        overallScore: finalScore,
        // Replace hardcoded technicalFit with derived score
        technicalFit: derived.score,
        feasibility: combinedFeasibility,
        details: {
          ...opt.scoreBreakdown.details,
          technicalSubScores: {
            // Replace with derived component scores
            ...derived.components,
            integration_complexity: integrationComplexity,
          },
          technicalSubScoreNotes: {
            ...(opt.scoreBreakdown.details?.technicalSubScoreNotes ?? {}),
            circuit_match: techCircuitMatchForOptionId(opt.id).reasons,
          },
          reasons: [
            // Derived reasons first, then original, then extras
            ...derived.reasons,
            ...(opt.scoreBreakdown.details?.reasons ?? []),
            ...extraReasons,
          ],
          explanationItems,
          debug: debugPayload,
        },
      } : undefined,
    };
  });
}

// ── Extract performance ranges from engine option ───────────────────

function buildPerformanceRanges(opt: RecommendationOption): OptionCardData['performanceRanges'] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = opt as any;
  const ranges: NonNullable<OptionCardData['performanceRanges']> = {};

  if (raw.performanceClaims?.scop) {
    ranges.scop = raw.performanceClaims.scop;
  }
  if (raw.performanceClaims?.scopConditional) {
    ranges.scopConditional = raw.performanceClaims.scopConditional;
  }
  if (opt.longevity?.expectedLifespanYears) {
    ranges.lifespan = {
      min: opt.longevity.expectedLifespanYears.min,
      max: opt.longevity.expectedLifespanYears.max,
      unit: 'years',
    };
  }

  return Object.keys(ranges).length > 0 ? ranges : undefined;
}

// ── Map engine recommendation to SystemOptionCard ───────────────────

function mapEngineOptionToCard(opt: RecommendationOption, normalizedInput: SystemRecInput): OptionCardData {
  // Prefer fit scoring output; fall back to engine score if fit score is unavailable.
  // Engine score may be legacy 0-10 or current 0-100 depending on source.
  const rawEngineScore = typeof opt.score === 'number' ? opt.score : null;
  const fallbackScore = rawEngineScore == null
    ? null
    : rawEngineScore <= 10 ? rawEngineScore * 10 : rawEngineScore;
  const score = opt.fitScore?.overall ?? fallbackScore;
  const suitability: OptionCardData['suitability'] = opt.excluded
    ? 'not-suitable'
    : (score ?? 0) >= 70 ? 'highest-standard' : 'possible-compromised';

  const breakdown = opt.fitScore?.breakdown;
  const circuitMatchNotes = (() => {
    const notes: string[] = [];
    const optionId = (opt.id || `${opt.boiler}-${opt.water}`).replace(/_/g, '-');
    const chCircuitType = normalizedInput.chCircuitType;
    const topology = normalizedInput.controlTopology;
    const heatLossKw = typeof normalizedInput.heatLossKw === 'number' ? normalizedInput.heatLossKw : null;
    const optionIsHeatPump = optionId.startsWith('ashp') || optionId.startsWith('gshp') || opt.boiler === 'heat_pump';
    const requiredCircuit = optionId.startsWith('regular-') ? 'open-vented'
      : optionId.startsWith('system-') ? 'sealed'
        : 'any';

    if (!chCircuitType || chCircuitType === 'unknown') {
      notes.push('circuit type unknown: neutral baseline used');
    } else if (requiredCircuit === 'any') {
      notes.push('no strict CH pressure regime required for this option');
    } else if (requiredCircuit === chCircuitType) {
      notes.push(`current ${chCircuitType} CH circuit matches option requirement`);
    } else if (requiredCircuit === 'sealed' && chCircuitType === 'open-vented') {
      notes.push('open-vented to sealed conversion needed for proposed option');
    } else if (requiredCircuit === 'open-vented' && chCircuitType === 'sealed') {
      notes.push('sealed to open-vented conversion required for this configuration');
    }

    if (topology === 'y-plan') {
      if ((heatLossKw != null && heatLossKw > 18) || optionIsHeatPump) {
        notes.push('Y-plan limits zoning/circulation headroom under higher-load design intent');
      } else {
        notes.push('Y-plan accepted for boiler systems at normal heat loss');
      }
    }

    if (requiredCircuit === 'open-vented' && !optionIsHeatPump && chCircuitType === 'open-vented') {
      notes.push('open-vented CH is treated as valid for gas boiler circulation fit (no default penalty)');
    }

    return notes;
  })();

  const explanationItems = rankExplanationItems(buildExplanationItems(opt, {
    reasons: [],
    assumptions: [],
    changes: [],
  }));

  const normalizedOptionId = (opt.id || `${opt.boiler}-${opt.water}`).replace(/_/g, '-');

  return {
    id: normalizedOptionId,
    label: opt.title || opt.boilerLabel || 'Unknown option',
    suitability,
    suitabilityScore: score == null ? null : Math.round(score),
    scoreLabel: typeof score === 'number' ? getScoreLabel(score) : opt.scoreLabel,
    retrofitScore: opt.retrofitScore ?? opt.fitScore?.retrofitScore,
    redesignedScore: opt.redesignedScore ?? opt.fitScore?.redesignedScore,
    architectureLevel: opt.architectureLevel ?? opt.fitScore?.architectureLevel,
    redesignRequirements: opt.redesignRequirements ?? opt.fitScore?.redesignRequirements,
    confidence: (opt.confidence as OptionCardData['confidence']) || 'medium',
    becauseBullets: explanationItems
      .slice(0, DEFAULT_VISIBLE_EXPLANATION_ITEMS)
      .map((item) => item.text),
    assumptions: opt.exclusionReason ? [opt.exclusionReason] : [],
    wouldNeedToChange: opt.cons?.slice(0, 3) || [],
    wontSolve: [],
    // Include score breakdown if available
    scoreBreakdown: opt.fitScore ? {
      overallScore: score == null ? 0 : Math.round(score),
      technicalFit: opt.fitScore.technicalFit,
      priorityFit: opt.fitScore.priorityFit,
      futureFit: opt.fitScore.futureFit,
      futureFitAssessed: opt.fitScore.futureFitAssessed,
      feasibility: opt.fitScore.feasibility,
      details: {
        ...(breakdown ?? {}),
        explanationItems,
        debug: isDevtoolsEnabled() ? {
          context: {
            optionId: opt.id,
            boiler: opt.boiler,
            water: opt.water,
            source: 'decision-engine',
          },
          engineInput: normalizedInput,
          constraints: {
            hardFails: (breakdown?.constraintReasons ?? []).filter((reason) => reason.kind === 'hard'),
            softConstraints: (breakdown?.constraintReasons ?? []).filter((reason) => reason.kind === 'soft'),
            all: breakdown?.constraintReasons ?? [],
            penalties: (breakdown as { penalties?: unknown } | undefined)?.penalties ?? [],
            killSwitchReasons: (breakdown as { killSwitchReasons?: unknown } | undefined)?.killSwitchReasons ?? [],
          },
          components: Object.entries((breakdown as { technicalSubScores?: Record<string, number> } | undefined)?.technicalSubScores ?? {}).map(([key, value]) => {
            const componentScore = Number.isFinite(value) ? Number(value) : null;
            const weight = (breakdown as {
              technicalAggregation?: { weightsByComponent?: Record<string, number> };
            } | undefined)?.technicalAggregation?.weightsByComponent?.[key] ?? 0;
            const multiplier = 1;
            const weightedContribution = componentScore == null || weight === 0
              ? 0
              : Number((componentScore * weight * multiplier).toFixed(2));

            return {
              component: key,
              score: componentScore,
              weight,
              multiplier,
              weightedContribution,
              notes: [
                `engine.${key}`,
                ...(((breakdown as { technicalSubScoreNotes?: Record<string, string[]> } | undefined)
                  ?.technicalSubScoreNotes?.[key]) ?? (key === 'circuit_match' ? circuitMatchNotes : [])),
                'sub-scores are weighted into technicalFit in fitScoring',
              ],
            };
          }),
          componentBreakdown: {
            technicalSubScores: (breakdown as { technicalSubScores?: Record<string, number> } | undefined)?.technicalSubScores ?? {},
            technicalSubScoreNotes: {
              ...((breakdown as { technicalSubScoreNotes?: Record<string, string[]> } | undefined)?.technicalSubScoreNotes ?? {}),
              circuit_match: ((breakdown as { technicalSubScoreNotes?: Record<string, string[]> } | undefined)
                ?.technicalSubScoreNotes?.circuit_match) ?? circuitMatchNotes,
            },
            priorityScores: breakdown?.priorityScores ?? {},
            futurePlanScores: breakdown?.futurePlanScores ?? {},
            reasons: breakdown?.reasons ?? [],
            confidenceAdjustments: breakdown?.confidenceAdjustments ?? [],
            penalties: (breakdown as { penalties?: unknown } | undefined)?.penalties ?? [],
            killSwitchReasons: (breakdown as { killSwitchReasons?: unknown } | undefined)?.killSwitchReasons ?? [],
          },
          aggregation: {
            technicalAggregation: (breakdown as {
              technicalAggregation?: {
                formula: string;
                weightsByComponent: Record<string, number>;
                preGateScore: number;
                gateMultiplier: number;
                gateReasons: string[];
                preClampScore: number;
                postClampScore: number;
                clampReasons: string[];
              };
            } | undefined)?.technicalAggregation,
            buckets: [
              { key: 'technicalFit', score: opt.fitScore.technicalFit, weight: 0.55, contribution: Number((opt.fitScore.technicalFit * 0.55).toFixed(2)) },
              { key: 'priorityFit', score: opt.fitScore.priorityFit, weight: 0.30, contribution: Number((opt.fitScore.priorityFit * 0.30).toFixed(2)) },
              { key: 'futureFit', score: opt.fitScore.futureFit, weight: opt.fitScore.futureFitAssessed ? 0.15 : 0, contribution: opt.fitScore.futureFit == null ? null : Number((opt.fitScore.futureFit * 0.15).toFixed(2)) },
            ],
            overall: {
              score: Math.round(score),
              formula: 'overall = feasibility × (0.55×technicalFit + 0.30×priorityFit + futureWeight×futureFit)',
            },
          },
        } : undefined,
      },
    } : undefined,
    // Performance ranges from engine (PRs #440-#442)
    performanceRanges: buildPerformanceRanges(opt),
  };
}


function buildCostOptimisedOptionC(optionA: OptionCardData): OptionCardData {
  const nextScore = Math.max(0, Math.round((optionA.scoreBreakdown?.overallScore ?? optionA.suitabilityScore ?? 0) - 6));
  return {
    ...optionA,
    id: `${optionA.id}__option_c`,
    label: `${optionA.label} (cost optimised)`,
    suitabilityScore: nextScore,
    becauseBullets: [
      'Keeps the same core architecture as Option A.',
      'Uses standard-spec components where premium upgrades are not mandatory.',
      'Removes non-essential scope while retaining safety and feasibility items.',
    ],
    wontSolve: [
      'May not deliver the same premium comfort/control extras as Option A.',
      ...optionA.wontSolve.slice(0, 2),
    ],
    scoreBreakdown: optionA.scoreBreakdown
      ? {
          ...optionA.scoreBreakdown,
          overallScore: nextScore,
        }
      : optionA.scoreBreakdown,
  };
}
function mapEngineResultToCards(result: DecisionEngineResult): OptionCardData[] {
  const cards: OptionCardData[] = [];
  const output = result.output;

  if (output.primary && !output.primary.excluded) {
    cards.push(mapEngineOptionToCard(output.primary, result.normalizedInput));
  }

  if (output.alternatives) {
    for (const alt of output.alternatives) {
      cards.push(mapEngineOptionToCard(alt, result.normalizedInput));
    }
  }

  if (output.consideredOptions) {
    for (const opt of output.consideredOptions) {
      // Avoid duplicates
      if (!cards.some(c => c.id === (opt.id || `${opt.boiler}-${opt.water}`))) {
        cards.push(mapEngineOptionToCard(opt, result.normalizedInput));
      }
    }
  }

  // Sort by rendered overall score (breakdown overall when available)
  cards.sort((a, b) => {
    const scoreA = a.scoreBreakdown?.overallScore ?? a.suitabilityScore ?? 0;
    const scoreB = b.scoreBreakdown?.overallScore ?? b.suitabilityScore ?? 0;
    return scoreB - scoreA;
  });

  if (cards.length > 0 && !cards.some((card) => card.id.endsWith("__option_c"))) {
    cards.splice(2, 0, buildCostOptimisedOptionC(cards[0]));
  }

  return cards;
}

// ── Current system SWOT derivation ──────────────────────────────────

function deriveCurrentSystemSWOT(sd: SystemDetailsData, wd: WaterData, em: EmittersData) {
  const sourceLabels: Record<string, string> = {
    gas: 'Gas boiler', oil: 'Oil boiler', lpg: 'LPG boiler', electric: 'Electric',
    'heat-pump': 'Heat pump', hybrid: 'Hybrid', unknown: 'Unknown system',
  };
  const label = sourceLabels[sd.heatSource] || 'Unknown system';

  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const threats: string[] = [];

  if (sd.heatSource === 'gas' || sd.heatSource === 'oil' || sd.heatSource === 'lpg') {
    strengths.push('Familiar technology, widely supported');
    strengths.push('Fast recovery / high flow temperatures');
  }
  if (sd.circuitType === 'sealed') strengths.push('Sealed system (modern, less corrosion)');
  if (sd.controlTopology === 's-plan' || sd.controlTopology === 's-plan-plus') {
    strengths.push('S-plan zoning (independent CH/DHW)');
  }

  if (sd.circuitType === 'open-vented') weaknesses.push('Open-vented — oxygen ingress promotes corrosion');
  if (sd.controlTopology === 'y-plan') weaknesses.push('Y-plan — mid-position compromise on CH/DHW');
  if (sd.controlTopology === 'gravity-dhw') weaknesses.push('Gravity DHW — slow recovery, poor control');
  if (sd.lowTempDesign === 'no') weaknesses.push('Not designed for low-temperature operation');
  if (em.performanceSignal === 'some-struggle') weaknesses.push('Some rooms struggle to heat');
  if (em.performanceSignal === 'many-struggle') weaknesses.push('Many rooms struggle to heat');
  if (em.primarySizeBand === '15mm') weaknesses.push('15 mm primaries may restrict flow');
  if (wd.standingLossBand === 'high') weaknesses.push('High standing losses from hot water storage');
  if (wd.cylinderTypeProfile === 'vintage-uninsulated') weaknesses.push('Old cylinder is causing avoidable heat loss');
  if (em.sludgeEvidence === 'likely' || em.sludgeEvidence === 'confirmed') {
    weaknesses.push('Dirty system water likely reducing efficiency and heat output');
  }

  if (sd.heatSource === 'gas' || sd.heatSource === 'oil') threats.push('Fossil fuel price volatility');
  if (sd.heatSource === 'oil' || sd.heatSource === 'lpg') threats.push('Potential future fuel availability risk');
  const hasBronze = wd.circulationMode === 'bronze-return-24-7' || wd.circulationMode === 'bronze-return-timed';
  if (hasBronze) threats.push('Bronze secondary return adds continuous losses');
  if (em.sludgeEvidence === 'likely' || em.sludgeEvidence === 'confirmed') {
    threats.push('Higher breakdown risk until system is cleaned and filtered');
  }

  return { label, strengths, weaknesses, threats };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatOptionMatchScore(option: OptionCardData | null | undefined): string {
  const score = option?.scoreBreakdown?.overallScore ?? option?.suitabilityScore;
  if (typeof score !== 'number' || !Number.isFinite(score)) return '—';
  return String(Math.round(Math.max(0, Math.min(100, score))));
}

function computeConfidence(data: any): ConfidenceBadge {
  const values = Object.values(data as Record<string, unknown>).filter(v => typeof v !== 'object');
  const filled = values.filter(v => v && v !== 'unknown' && v !== '');
  const ratio = values.length > 0 ? filled.length / values.length : 0;
  if (ratio >= 0.7) return { level: 'high', label: `${Math.round(ratio * 100)}% filled` };
  if (ratio >= 0.4) return { level: 'medium', label: `${Math.round(ratio * 100)}% filled` };
  return { level: 'low', label: `${Math.round(ratio * 100)}% filled` };
}

// ── Build snapshot for D1 ───────────────────────────────────────────

interface HubSnapshot {
  heatDemand: HeatDemandData;
  systemDetails: SystemDetailsData;
  water: WaterData;
  emitters: EmittersData;
  dhwPressure: DHWPressureData;
  priorities: Array<{ key: string; rank: number }>;
  futurePlans: FuturePlan[];
  urgency?: string;
  disruptionTolerance?: string;
  selectedOptionId: string | null;
  selectedOptionAddOns: string[];
  explainerStates: ReturnType<typeof serializeExplainerState>;
  scopeSnapshot: ScopeSnapshot | null;
}

const HUB_DRAFT_STORAGE_KEY = 'system-rec:hub:draft';

function buildHubStorySnapshot(hub: HubSnapshot): StorySnapshot {
  // Infer boiler type from dhwArchitecture and circuitType using shared utility
  // This ensures rawInput matches hub-collected data
  const inferredBoilerType = inferBoilerTypeFromHub(
    hub.systemDetails.dhwArchitecture,
    hub.systemDetails.circuitType,
    hub.systemDetails.heatSource
  );

  const rawInput = {
    propertyType: hub.heatDemand.dwellingForm,
    propertyAgeBand: hub.heatDemand.fabricEra,
    bedrooms: hub.heatDemand.bedrooms,
    bathrooms: hub.heatDemand.bathrooms || '', availableSpace: '', outdoorSpace: '',
    occupancyCount: hub.water.occupants,
    occupancyTiming: '', temporaryGuests: '', futureDemand: '',
    hotWaterDemand: '', usagePattern: '', usageFrequency: '',
    boilerType: inferredBoilerType,
    waterSystem: hub.water.dhwSystemType,
    electricalCapacity: '', mainsPressure: hub.dhwPressure.mainsStaticPressureBar != null ? String(hub.dhwPressure.mainsStaticPressureBar) : '', flowRate: hub.dhwPressure.mainsFlowLpm != null ? String(hub.dhwPressure.mainsFlowLpm) : '', drawsPerDay: '',
    mainsFlowLpm: hub.dhwPressure.mainsFlowLpm,
    mainsStaticPressureBar: hub.dhwPressure.mainsStaticPressureBar,
    mainsDynamicFlowLpm: hub.dhwPressure.mainsDynamicFlowLpm,
    mainsDynamicPressureBar: hub.dhwPressure.mainsDynamicPressureBar,
    coldMainType: hub.dhwPressure.coldMainType,
    systemAge: hub.systemDetails.systemAge, 
    efficiencySource: 'unknown',
    sedbukRating: hub.systemDetails.sedbukRating, 
    sedbukBand: hub.systemDetails.sedbukBand, 
    gcNumber: hub.systemDetails.gcNumber, 
    boilerMake: hub.systemDetails.boilerMake, 
    boilerModel: hub.systemDetails.boilerModel,
    epcBand: '',
    currentFuelType: hub.systemDetails.heatSource === 'unknown' ? 'gas' : hub.systemDetails.heatSource,
    sludgeRisk: 'unknown',
    currentThermostatType: hub.systemDetails.temperatureControls !== 'unknown' ? hub.systemDetails.temperatureControls : '',
    lengthOfStay: '', lifeStage: '', budgetPriority: '', disruptionTolerance: '', urgency: '',
    upgradePriorities: hub.priorities,
    futurePlans: hub.futurePlans,
    emitterType: hub.emitters.emitters,
    emitterLowTempSuitability: hub.systemDetails.lowTempDesign === 'yes' ? 'likely_ok' : 'unknown',
    pipeBore: hub.emitters.primarySizeBand === 'unknown' ? 'unknown' : hub.emitters.primarySizeBand,
    systemType: hub.systemDetails.circuitType === 'unknown' ? undefined : hub.systemDetails.circuitType,
    insulationLevel:
      hub.heatDemand.retrofitLevel === 'full' ? 'good'
        : hub.heatDemand.retrofitLevel === 'partial' ? 'average'
          : inferInsulationFromFabricEra(hub.heatDemand.fabricEra) === 'high' ? 'good'
            : inferInsulationFromFabricEra(hub.heatDemand.fabricEra) === 'medium' ? 'average'
              : inferInsulationFromFabricEra(hub.heatDemand.fabricEra) === 'poor' ? 'poor'
                : 'unknown',
    // Hub-specific extensions (for portal rehydration)
    _hubData: {
      heatDemand: hub.heatDemand,
      systemDetails: hub.systemDetails,
      water: hub.water,
      emitters: hub.emitters,
      dhwPressure: hub.dhwPressure,
      priorities: hub.priorities,
      futurePlans: hub.futurePlans,
      urgency: hub.urgency,
      disruptionTolerance: hub.disruptionTolerance,
      explainerStates: hub.explainerStates,
      scopeSnapshot: hub.scopeSnapshot,
    },
  } as CollectedData & { _hubData: unknown };

  return buildStorySnapshot({
    rawInput: rawInput as CollectedData,
    engineOutput: null,
    selectedOptionId: hub.selectedOptionId,
    selectedOptionAddOns: hub.selectedOptionAddOns,
  });
}


function parseStandingLossKwhPerDay(value: string): number | undefined {
  if (!value) return undefined;
  const matches = Array.from(value.matchAll(/\d+(?:\.\d+)?/g)).map((m) => Number.parseFloat(m[0])).filter((num) => Number.isFinite(num));
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  return (matches[0] + matches[1]) / 2;
}

function deriveStandingLossBand(kwhPerDay: number | undefined): 'low' | 'medium' | 'high' | 'unknown' {
  if (typeof kwhPerDay !== 'number' || !Number.isFinite(kwhPerDay)) return 'unknown';
  if (kwhPerDay <= 1.0) return 'low';
  if (kwhPerDay <= 1.8) return 'medium';
  return 'high';
}

function mapFabricEraToPropertyAgeBand(fabricEra: HeatDemandData['fabricEra']): SystemRecInput['propertyAgeBand'] {
  switch (fabricEra) {
    case 'pre-1919': return 'pre_1919';
    case '1919-1944': return '1919_1944';
    case '1945-1964': return '1945_1964';
    case '1965-1980': return '1965_1980';
    case '1981-1995': return '1981_2000';
    case '1996-2006': return '2001_2010';
    case '2007-2013':
    case '2013-present': return '2011_plus';
    default: return 'unknown';
  }
}

function mapDwellingToPropertyType(dwellingForm: HeatDemandData['dwellingForm']): string {
  switch (dwellingForm) {
    case 'semi-detached': return 'semi';
    case 'mid-terrace':
    case 'end-terrace': return 'terraced';
    case 'flat-top':
    case 'flat-mid': return 'flat';
    default: return dwellingForm === 'unknown' ? 'semi' : dwellingForm;
  }
}

function mapDhwSystemTypeToWater(dhwSystemType: WaterData['dhwSystemType']): string {
  switch (dhwSystemType) {
    case 'combi-on-demand': return 'on_demand';
    case 'vented-cylinder': return 'open_vented';
    case 'unvented-cylinder': return 'unvented';
    case 'thermal-store': return 'thermal_store';
    default: return 'open_vented';
  }
}

function inferInsulationFromFabricEra(
  fabricEra: HeatDemandData['fabricEra'],
): 'poor' | 'medium' | 'high' | undefined {
  switch (fabricEra) {
    case 'pre-1919':
    case '1919-1944':
    case '1945-1964':
      return 'poor';
    case '1965-1980':
    case '1981-1995':
      return 'medium';
    case '1996-2006':
    case '2007-2013':
    case '2013-present':
      return 'high';
    default:
      return undefined;
  }
}

function mapSelectedOptionToCompareOption(selectedOptionId: string | null): { boiler: string; water: string }[] | undefined {
  if (!selectedOptionId) return undefined;

  const normalizedId = selectedOptionId.replace(/_/g, '-');

  const mapping: Record<string, { boiler: string; water: string }> = {
    'regular-open-vented': { boiler: 'regular', water: 'open_vented' },
    'regular-mixergy-open-vented': { boiler: 'regular', water: 'open_vented' },
    'regular-unvented': { boiler: 'regular', water: 'unvented' },
    'regular-mixergy-unvented': { boiler: 'regular', water: 'unvented' },
    'system-open-vented': { boiler: 'system', water: 'open_vented' },
    'system-mixergy-open-vented': { boiler: 'system', water: 'open_vented' },
    'system-unvented': { boiler: 'system', water: 'unvented' },
    'system-mixergy-unvented': { boiler: 'system', water: 'unvented' },
    'combi': { boiler: 'combi', water: 'on_demand' },
    'storage-combi': { boiler: 'storage_combi', water: 'storage_combi' },
    'ashp-unvented': { boiler: 'heat_pump', water: 'unvented' },
    'gshp-unvented': { boiler: 'heat_pump', water: 'unvented' },
  };

  const selected = mapping[normalizedId];
  return selected ? [selected] : undefined;
}

/**
 * Parse boiler output string to kW value
 * Handles formats like: "<8kW", "12kW", "12-15kW", "12", etc.
 */
function parseBoilerOutputToKw(output: string): number | undefined {
  if (!output || output === 'unknown') return undefined;
  
  const normalized = output.trim().toLowerCase();
  
  // Handle <8kW format - return the value just below the threshold
  // (e.g., "<8kW" becomes 7kW, representing typical small boilers)
  if (normalized.startsWith('<')) {
    const match = normalized.match(/(\d+)/);
    if (match) return Number.parseInt(match[1], 10) - 1;
  }
  
  // Handle range format like "12-15kW" - take the lower bound
  const rangeMatch = normalized.match(/(\d+)-(\d+)/);
  if (rangeMatch) {
    return Number.parseInt(rangeMatch[1], 10);
  }
  
  // Handle simple numeric format
  const simpleMatch = normalized.match(/(\d+)/);
  if (simpleMatch) {
    return Number.parseInt(simpleMatch[1], 10);
  }
  
  return undefined;
}

function buildHubEngineInput(hub: {
  heatDemand: HeatDemandData;
  systemDetails: SystemDetailsData;
  water: WaterData;
  emitters: EmittersData;
  dhwPressure: DHWPressureData;
  priorities: Array<{ key: string; rank: number }>;
  futurePlans: FuturePlan[];
  urgency?: string;
  disruptionTolerance?: string;
  selectedOptionId: string | null;
}): SystemRecInput {
  const bedrooms = Number.parseInt(hub.heatDemand.bedrooms, 10);
  const bathrooms = Number.parseInt(hub.heatDemand.bathrooms, 10);
  const occupancy = Number.parseInt(hub.water.occupants, 10);

  // Cold mains values are now number | null, no parsing needed
  const mainsFlow = hub.dhwPressure.mainsFlowLpm;
  const mainsStatic = hub.dhwPressure.mainsStaticPressureBar;
  const mainsDynamicFlow = hub.dhwPressure.mainsDynamicFlowLpm;
  const mainsDynamicPressure = hub.dhwPressure.mainsDynamicPressureBar;

  const inferredCurrentBoiler = hub.systemDetails.dhwArchitecture === 'combi-on-demand'
    ? 'combi'
    : hub.systemDetails.circuitType === 'sealed'
      ? 'system'
      : 'regular';

  // Gas availability - infer from heat source
  const gasAvailable = hub.systemDetails.heatSource === 'gas' ||
                       hub.systemDetails.heatSource === 'lpg';

  // Insulation level - map from retrofit level
  const inferredInsulation = inferInsulationFromFabricEra(hub.heatDemand.fabricEra);
  const insulationLevel = hub.heatDemand.retrofitLevel === 'full' ? 'high'
                        : hub.heatDemand.retrofitLevel === 'partial' ? 'medium'
                        : hub.heatDemand.retrofitLevel === 'none' ? 'poor'
                        : inferredInsulation;

  // Boiler output - parse from systemDetails if available
  const boilerOutputKw = hub.systemDetails.boilerOutput && hub.systemDetails.boilerOutput !== 'unknown'
    ? parseBoilerOutputToKw(hub.systemDetails.boilerOutput)
    : undefined;

  // Build currentSystemEfficiency from collected SEDBUK/efficiency data
  const efficiency: SystemRecInput['currentSystemEfficiency'] = {};
  let hasEfficiencyData = false;

  if (hub.systemDetails.gcNumber) {
    efficiency.gcNumber = hub.systemDetails.gcNumber;
    hasEfficiencyData = true;
  }
  if (hub.systemDetails.boilerMake) {
    efficiency.boilerMake = hub.systemDetails.boilerMake;
    hasEfficiencyData = true;
  }
  if (hub.systemDetails.boilerModel) {
    efficiency.boilerModel = hub.systemDetails.boilerModel;
    hasEfficiencyData = true;
  }
  if (boilerOutputKw) {
    efficiency.boilerOutputKw = boilerOutputKw;
    hasEfficiencyData = true;
  }
  if (hub.systemDetails.sedbukRating) {
    const rating = Number.parseFloat(hub.systemDetails.sedbukRating);
    if (Number.isFinite(rating) && rating > 0) {
      efficiency.sedbukSeasonal = rating > 1 ? rating / 100 : rating;
      efficiency.source = 'SEDBUK';
      hasEfficiencyData = true;
    }
  }
  if (hub.systemDetails.sedbukBand && hub.systemDetails.sedbukBand !== 'unknown') {
    efficiency.sedbukBand = hub.systemDetails.sedbukBand.toUpperCase() as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
    if (!efficiency.source) efficiency.source = 'SEDBUK';
    hasEfficiencyData = true;
  }
  if (!efficiency.source && hasEfficiencyData) {
    efficiency.source = 'ASSUMED';
  }

  // Build emitters sub-object for engine
  const emittersInput: SystemRecInput['emitters'] = {
    type: hub.emitters.emitters === 'ufh' ? 'underfloor'
      : hub.emitters.emitters === 'mixed' ? 'mixed'
      : hub.emitters.emitters === 'radiators' ? 'radiators'
      : 'unknown',
    // Engine lowTempSuitability: likely_ok | likely_needs_upgrade | definitely_needs_upgrade | unknown
    // (performanceClaims.ts isLowTempConfirmed checks === 'likely_ok')
    lowTempSuitability: hub.emitters.lowTempCapability === 'yes' ? 'likely_ok'
      : hub.emitters.lowTempCapability === 'no' ? 'likely_needs_upgrade'
      : 'unknown',
    roomsStruggleToHeat: hub.emitters.performanceSignal === 'some-struggle' || hub.emitters.performanceSignal === 'many-struggle' ? 'yes'
      : hub.emitters.performanceSignal === 'all-heat-well' ? 'no'
      : 'unknown',
    pipeBore: hub.emitters.primarySizeBand !== 'unknown' ? hub.emitters.primarySizeBand as '15mm' | '22mm' : 'unknown',
  };

  // Map water hardness
  const waterHardness = hub.water.hardnessBand !== 'unknown' ? hub.water.hardnessBand as SystemRecInput['waterHardness'] : undefined;

  // Map system age to years
  const systemAgeStr = hub.systemDetails.systemAge;
  let existingSystemAge: number | undefined;
  if (systemAgeStr) {
    const ageNum = Number.parseInt(systemAgeStr, 10);
    if (Number.isFinite(ageNum)) existingSystemAge = ageNum;
  }

  // Map urgency and disruption tolerance (from priorities screen or defaults)
  const urgency = hub.urgency && hub.urgency !== 'unknown'
    ? hub.urgency as SystemRecInput['urgency']
    : 'planning';
  const disruptionTolerance = hub.disruptionTolerance && hub.disruptionTolerance !== 'unknown'
    ? hub.disruptionTolerance as SystemRecInput['disruptionTolerance']
    : 'moderate';

  // Map circuit type for both legacy and new fields
  const rawCircuitType = hub.systemDetails.circuitType;
  const chCircuitType: 'sealed' | 'open-vented' | 'unknown' = 
    rawCircuitType === 'sealed' || rawCircuitType === 'open-vented' ? rawCircuitType : 'unknown';
  const systemAlreadySealed = rawCircuitType === 'sealed' ? 'yes'
    : rawCircuitType === 'open-vented' ? 'no'
    : 'unknown';

  // Map Mixergy cylinder type to hotWater variant
  // This enables Mixergy options (H, I, J, K, N, O) in the decision engine
  let hotWater: SystemRecInput['hotWater'] | undefined;
  const cylinderType = hub.water.cylinderTypeProfile;
  if (cylinderType === 'mixergy-vented' || cylinderType === 'mixergy-unvented') {
    hotWater = {
      strategy: cylinderType === 'mixergy-vented' ? 'open_vented' : 'unvented',
      variant: 'mixergy',
    };
  }

  return {
    propertyType: mapDwellingToPropertyType(hub.heatDemand.dwellingForm),
    propertyAgeBand: mapFabricEraToPropertyAgeBand(hub.heatDemand.fabricEra),
    bedrooms: Number.isFinite(bedrooms) ? bedrooms : 3,
    bathrooms: Number.isFinite(bathrooms) ? bathrooms : 1,
    occupancy: Number.isFinite(occupancy) ? occupancy : 3,
    hotWaterDemand: occupancy >= 4 ? 'high' : occupancy >= 2 ? 'medium' : 'low',
    drawsPerDay: Number.isFinite(occupancy) ? Math.max(3, occupancy * 2) : 6,
    currentBoiler: inferredCurrentBoiler,
    currentWater: mapDhwSystemTypeToWater(hub.water.dhwSystemType),
    expectedLengthOfStay: 'medium',
    electricalCapacityConfidence: hub.systemDetails.heatSource === 'heat-pump' ? 'likely_ok' : 'unknown',
    disruptionTolerance,
    costPriority: 'balanced',
    upgradePriorities: hub.priorities.map(p => ({ key: p.key as any, rank: p.rank })),
    futurePlans: hub.futurePlans || [],
    urgency,
    futureDemand: 'stable',
    temporaryGuests: 'occasional',
    mainsFlowLpm: mainsFlow !== null ? mainsFlow : undefined,
    mainsStaticPressureBar: mainsStatic !== null ? mainsStatic : undefined,
    mainsDynamicFlowLpm: mainsDynamicFlow !== null ? mainsDynamicFlow : undefined,
    mainsDynamicPressureBar: mainsDynamicPressure !== null ? mainsDynamicPressure : undefined,
    coldMainType: hub.dhwPressure.coldMainType !== 'unknown' ? hub.dhwPressure.coldMainType : undefined,
    heatEmitterType: hub.emitters.emitters === 'ufh' ? 'underfloor_heating' : 'standard_rads',
    compareOptions: mapSelectedOptionToCompareOption(hub.selectedOptionId),
    baselineEnabled: true,
    gasAvailable: gasAvailable ? gasAvailable : undefined,
    cylinderSpace: hub.systemDetails.cylinderSpace === 'yes-existing' || hub.systemDetails.cylinderSpace === 'yes-available'
      ? true
      : hub.systemDetails.cylinderSpace === 'no'
        ? false
        : undefined,
    standingLossBand: hub.water.standingLossBand,
    currentStandingLossKwhPerDay: hub.water.currentStandingLossKwhPerDay,
    proposedStandingLossKwhPerDay: hub.water.proposedStandingLossKwhPerDay,
    standingLossSource: hub.water.standingLossSource,
    insulationLevel,
    currentSystemEfficiency: hasEfficiencyData ? efficiency : undefined,
    currentFuelType: hub.systemDetails.heatSource !== 'unknown' && hub.systemDetails.heatSource !== 'heat-pump' && hub.systemDetails.heatSource !== 'hybrid'
      ? hub.systemDetails.heatSource as 'gas' | 'oil' | 'electric' | 'lpg'
      : 'gas',
    emitters: emittersInput,
    waterHardness,
    sludgeRisk: hub.emitters.sludgeEvidence !== 'unknown'
      ? hub.emitters.sludgeEvidence as SystemRecInput['sludgeRisk']
      : undefined,
    existingSystemAge,
    currentThermostatType: hub.systemDetails.temperatureControls !== 'unknown'
      ? hub.systemDetails.temperatureControls as SystemRecInput['currentThermostatType']
      : undefined,
    systemAlreadySealed,
    chCircuitType,
    hotWater,
    // Map temperatureControls → controlsPackagePreference (engine controls enum)
    controlsPackagePreference: (() => {
      switch (hub.systemDetails.temperatureControls) {
        case 'basic':
        case 'programmable-stat':
          return 'basic_programmer';
        case 'smart':
          return 'smart_zonal';
        case 'load-compensation':
          return 'weather_comp';
        case 'smart-load-compensation':
          return 'open_therm';
        default:
          return undefined;
      }
    })(),
    // Default tariffProfile to flat unless data allows inference
    tariffProfile: 'flat' as const,
  };
}

// ── Main component ──────────────────────────────────────────────────

export function HubPage() {
  const { docId: routeDocId } = useParams<{ docId: string }>();
  const [searchParams] = useSearchParams();
  const queryDocId = searchParams.get('docId') ?? undefined;
  const effectiveDocId = routeDocId ?? queryDocId;

  // Page state
  const [activePage, setActivePage] = useState<ActivePage>('system-choice');
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('none');

  // Tile data
  const [heatDemand, setHeatDemand] = useState<HeatDemandData>(DEFAULT_HEAT_DEMAND);
  const [systemDetails, setSystemDetails] = useState<SystemDetailsData>(DEFAULT_SYSTEM_DETAILS);
  const [water, setWater] = useState<WaterData>(DEFAULT_WATER);
  const [emitters, setEmitters] = useState<EmittersData>(DEFAULT_EMITTERS);
  const [dhwPressure, setDhwPressure] = useState<DHWPressureData>(DEFAULT_DHW_PRESSURE);
  const [priorities, setPriorities] = useState<Array<{ key: string; rank: number }>>([]);
  const [futurePlans, setFuturePlans] = useState<FuturePlan[]>([]);
  const [urgency, setUrgency] = useState<string>('');
  const [disruptionTolerance, setDisruptionTolerance] = useState<string>('');
  const [showUpgradeComparison, setShowUpgradeComparison] = useState(false);

  // Selected targets (Options A, B, and C — all user-selected)
  const [selectedOptionA, setSelectedOptionA] = useState<string | null>(null);
  const [selectedOptionB, setSelectedOptionB] = useState<string | null>(null);
  const [selectedOptionC, setSelectedOptionC] = useState<string | null>(null);
  // Backwards-compat: first selected option id for persistence / engine
  const selectedOptionId = selectedOptionA;

  // Premium add-ons for selected option only
  const [selectedOptionAddOns, setSelectedOptionAddOns] = useState<ScopeItem[]>(() => getDefaultOptionalItems('complete'));

  // Explainers
  const [explainers, setExplainers] = useState<Explainer[]>(() =>
    DEFAULT_EXPLAINERS.map(e => ({ ...e }))
  );

  // Scope (default to complete spec for backward compatibility)
  const [optionalItems, setOptionalItems] = useState<ScopeItem[]>(() => getDefaultOptionalItems('complete'));

  // Engine state
  const [engineResult, setEngineResult] = useState<DecisionEngineResult | null>(null);
  const [engineRunning, setEngineRunning] = useState(false);
  const engineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persistence
  const [docId, setDocId] = useState<string | undefined>(effectiveDocId);
  const [saving, setSaving] = useState(false);
  const [saveStatusLabel, setSaveStatusLabel] = useState<string>('Saving...');
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const isLoadedRef = useRef(false);
  const inFlightSaveRef = useRef<Promise<string> | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Validation state
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Scroll navigation
  const comparisonRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [showScrollNav, setShowScrollNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollNav(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchParams.get('explainer')) {
      setActivePage('explainers');
    }
  }, [searchParams]);

  // Load existing document from D1
  useEffect(() => {
    if (!effectiveDocId || isLoadedRef.current) return;
    isLoadedRef.current = true;

    retrieveDocument(effectiveDocId).then(snapshot => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = snapshot.rawInput as any;
      if (raw?._hubData) {
        const h = raw._hubData;
        if (h.heatDemand) setHeatDemand(h.heatDemand);

        const loadedSystemDetails = h.systemDetails || DEFAULT_SYSTEM_DETAILS;
        const loadedEmitters = h.emitters || DEFAULT_EMITTERS;
        const normalizedLoadedEmitters = applyEmitterDefault(loadedEmitters, loadedSystemDetails, computeConfidence);

        if (h.systemDetails) setSystemDetails(loadedSystemDetails);
        if (h.water) setWater(h.water);
        setEmitters(normalizedLoadedEmitters);
        if (h.dhwPressure) setDhwPressure(h.dhwPressure);
        if (h.priorities) setPriorities(h.priorities);
        if (h.futurePlans) setFuturePlans(h.futurePlans);
        if (h.urgency) setUrgency(h.urgency);
        if (h.disruptionTolerance) setDisruptionTolerance(h.disruptionTolerance);
        if (h.explainerStates) {
          setExplainers(prev => rehydrateExplainerState(prev, h.explainerStates));
        }
        if (h.scopeSnapshot) {
          const savedOpt = [...(h.scopeSnapshot.selectedScope || []), ...(h.scopeSnapshot.excludedScope || [])];
          if (savedOpt.length > 0) setOptionalItems(savedOpt);
        }
      }
      if (snapshot.selectedOptionId) setSelectedOptionA(snapshot.selectedOptionId);
      if (snapshot.selectedOptionAddOns?.length) {
        const ids = new Set(snapshot.selectedOptionAddOns);
        setSelectedOptionAddOns(getDefaultOptionalItems('complete').map(item => ids.has(item.id) ? { ...item, status: 'included' } : { ...item, status: 'excluded' }));
      }
      setDocId(effectiveDocId);
    }).catch(err => {
      console.warn('[hub] Failed to load document:', err);
    });
  }, [effectiveDocId]);

  useEffect(() => {
    if (effectiveDocId) return;
    try {
      const raw = sessionStorage.getItem(HUB_DRAFT_STORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as Partial<HubSnapshot>;
      if (draft.heatDemand) setHeatDemand(draft.heatDemand);
      const draftSystemDetails = draft.systemDetails || DEFAULT_SYSTEM_DETAILS;
      const draftEmitters = draft.emitters || DEFAULT_EMITTERS;
      const normalizedDraftEmitters = applyEmitterDefault(draftEmitters, draftSystemDetails, computeConfidence);

      if (draft.systemDetails) setSystemDetails(draftSystemDetails);
      if (draft.water) setWater(draft.water);
      setEmitters(normalizedDraftEmitters);
      if (draft.dhwPressure) setDhwPressure(draft.dhwPressure);
      if (draft.priorities) setPriorities(draft.priorities);
      if (draft.futurePlans) setFuturePlans(draft.futurePlans);
      if (draft.urgency) setUrgency(draft.urgency);
      if (draft.disruptionTolerance) setDisruptionTolerance(draft.disruptionTolerance);
      if (draft.selectedOptionId) setSelectedOptionA(draft.selectedOptionId);
    } catch (err) {
      console.warn('[hub] Failed to restore draft:', err);
    }
  }, [effectiveDocId]);

  // Check if enough data to run engine (at minimum: property type + bedrooms)
  const hasMinimumData = heatDemand.dwellingForm !== 'unknown' && heatDemand.bedrooms !== '';

  // Reactive engine: run engine on tile data changes (debounced 800ms)
  useEffect(() => {
    if (!hasMinimumData) {
      setEngineResult(null);
      return;
    }

    if (engineTimerRef.current) {
      clearTimeout(engineTimerRef.current);
    }

    engineTimerRef.current = setTimeout(async () => {
      setEngineRunning(true);
      try {
        const engineInput = buildHubEngineInput({
          heatDemand,
          systemDetails,
          water,
          emitters,
          dhwPressure,
          priorities,
          futurePlans,
          selectedOptionId,
          urgency,
          disruptionTolerance,
        });
        const result = await runDecisionEngine(engineInput);
        setEngineResult(result);
      } catch (err) {
        console.warn('[hub] Engine run failed, falling back to local derivation:', err);
        setEngineResult(null);
      } finally {
        setEngineRunning(false);
      }
    }, 800);

    return () => {
      if (engineTimerRef.current) {
        clearTimeout(engineTimerRef.current);
      }
    };
  }, [hasMinimumData, heatDemand, systemDetails, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId]);

  // Derived — prefer engine-normalized options, fall back to local derivation
  const localSystemOptions = deriveSystemOptions(heatDemand, systemDetails, water, emitters, dhwPressure, priorities);
  const systemOptions = engineResult
    ? mapEngineResultToCards(engineResult)
    : localSystemOptions;
  const selectedOption = systemOptions.find(o => o.id === selectedOptionA) ?? null;
  const selectedOptionBCard = systemOptions.find(o => o.id === selectedOptionB) ?? null;
  const selectedOptionCCard = systemOptions.find(o => o.id === selectedOptionC) ?? null;
  const currentSystem = deriveCurrentSystemSWOT(systemDetails, water, emitters);
  const starredExplainers = getStarredExplainers(explainers);
  const scopeSnapshot = selectedOptionA
    ? buildScopeSnapshot(selectedOptionA, selectedOption?.label ?? '', optionalItems)
    : null;

  useEffect(() => {
    if (docId) return;
    const snapshot: HubSnapshot = {
      heatDemand,
      systemDetails,
      water,
      emitters,
      dhwPressure,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    };
    sessionStorage.setItem(HUB_DRAFT_STORAGE_KEY, JSON.stringify(snapshot));
  }, [docId, heatDemand, systemDetails, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot]);

  // Monitor modal state and notify DevSidebar for auto-collapse
  useEffect(() => {
    if (activeScreen !== 'none') {
      notifyModalOpened();
    } else {
      notifyModalClosed();
    }
  }, [activeScreen]);

  const canPublish = selectedOptionA !== null;
  const engineStatus: 'idle' | 'stale' | 'current' =
    engineResult ? 'current' : hasMinimumData ? 'stale' : 'idle';

  // Persistence handlers
  const ensureSavedDocAndReport = useCallback(async (): Promise<string> => {
    if (inFlightSaveRef.current) {
      return inFlightSaveRef.current;
    }

    const savePromise = (async () => {
    // Validate before saving
    const validation = validateFormData(
      heatDemand,
      systemDetails,
      water,
      emitters,
      dhwPressure,
      priorities,
      selectedOptionA
    );

    if (!validation.isValid) {
      setValidationErrors(validation.missingFields);
      setShowValidationErrors(true);
      showToast(`Missing required fields. Please complete the assessment.`, 'error');
      throw new Error('Validation failed');
    }

    // Clear any validation errors
    setShowValidationErrors(false);
    setValidationErrors([]);

    setSaving(true);
    setSaveStatusLabel('Saving...');
    try {
      const engineInput = buildHubEngineInput({
        heatDemand,
        systemDetails,
        water,
        emitters,
        dhwPressure,
        priorities,
        selectedOptionId,
        urgency,
        disruptionTolerance,
      });
      const recommendation = await runDecisionEngine(engineInput);

      const snapshot = buildHubStorySnapshot({
        heatDemand, systemDetails, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance,
        selectedOptionId,
        selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
        explainerStates: serializeExplainerState(explainers),
        scopeSnapshot,
      });

      snapshot.engineOutput = {
        input: recommendation.normalizedInput,
        output: recommendation.output,
        generatedAt: new Date().toISOString(),
      };

      let result: PersistResponse;
      if (docId) {
        result = await updateDocument(docId, snapshot);
      } else {
        result = await persistDocument(snapshot);
      }
      setDocId(result.docId);
      setLastSaved(new Date().toISOString());
      sessionStorage.removeItem(HUB_DRAFT_STORAGE_KEY);
      showToast('Report saved successfully', 'success');

      // Scroll to comparison anchor after save
      if (comparisonRef.current) {
        comparisonRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return result.docId;
    } catch (err) {
      console.error('[hub] Save failed:', err);
      showToast('Save failed — check console for details', 'error');
      throw err;
    } finally {
      setSaving(false);
      inFlightSaveRef.current = null;
    }
    })();

    inFlightSaveRef.current = savePromise;
    return savePromise;
  }, [heatDemand, systemDetails, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, selectedOptionA, explainers, scopeSnapshot, docId, showToast]);

  const handleSave = useCallback(async () => {
    await ensureSavedDocAndReport();
  }, [ensureSavedDocAndReport]);

  const handleViewPortal = useCallback(async () => {
    // Pre-open a blank window synchronously from the click event so iOS Safari
    // doesn't block it as a popup (window.open after await is treated as popup).
    const win = window.open('about:blank', '_blank');
    try {
      setSaveStatusLabel('Saving...');
      const savedDocId = await ensureSavedDocAndReport();
      setSaveStatusLabel('Opening portal...');
      const portalUrl = makePortalUrl(savedDocId, window.location.origin);
      if (win) {
        win.location.href = portalUrl;
      } else {
        window.location.assign(portalUrl);
      }
    } catch {
      if (win) win.close();
      // toast already handled by save pipeline
    }
  }, [ensureSavedDocAndReport]);

  // Tile save handlers
  const saveHeatDemand = useCallback((d: HeatDemandData) => {
    const normalized = { ...d, heatLossBand: deriveHeatLossBand(d) };
    setHeatDemand({ ...normalized, confidence: computeConfidence(normalized) });
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:saveHeatDemand', {
      section: 'heat_demand',
      fields: Object.keys(d),
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand: normalized,
      systemDetails,
      water,
      emitters,
      dhwPressure,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [systemDetails, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot, docId]);
  const saveSystemDetails = useCallback((d: SystemDetailsData) => {
    const normalizedEmitters = applyEmitterDefault(emitters, d, computeConfidence);

    setSystemDetails({ ...d, confidence: computeConfidence(d) });
    if (normalizedEmitters !== emitters) {
      setEmitters(normalizedEmitters);
    }
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:saveSystemDetails', {
      section: 'system_details',
      fields: Object.keys(d),
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand,
      systemDetails: d,
      water,
      emitters: normalizedEmitters,
      dhwPressure,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [heatDemand, water, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot, docId]);
  const saveWater = useCallback((d: WaterData) => {
    const parsedStandingLoss = parseStandingLossKwhPerDay(d.standingLoss);
    const normalizedStandingBand = d.standingLossBand !== 'unknown'
      ? d.standingLossBand
      : deriveStandingLossBand(parsedStandingLoss);
    const normalizedEra = d.cylinderTypeProfile === 'high-performance' ? 'blue'
      : d.cylinderTypeProfile === 'standard-modern' || d.cylinderTypeProfile === 'legacy-vented' ? 'green'
        : d.cylinderTypeProfile === 'vintage-uninsulated' ? 'red'
          : 'unknown';
    const currentStandingLoss = typeof d.currentStandingLossKwhPerDay === 'number' && Number.isFinite(d.currentStandingLossKwhPerDay)
      ? d.currentStandingLossKwhPerDay
      : parsedStandingLoss;
    const normalized = {
      ...d,
      standingLossBand: normalizedStandingBand as 'low' | 'medium' | 'high' | 'unknown',
      cylinderEra: normalizedEra as 'green' | 'blue' | 'red' | 'unknown',
      currentStandingLossKwhPerDay: currentStandingLoss,
      standingLossSource: currentStandingLoss !== undefined ? 'estimated_from_band' as const : 'assumed_default' as const,
    };
    setWater({ ...normalized, confidence: computeConfidence(normalized) });
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:saveWater', {
      section: 'water',
      fields: Object.keys(d),
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand,
      systemDetails,
      water: normalized,
      emitters,
      dhwPressure,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [heatDemand, systemDetails, emitters, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot, docId]);
  const saveEmitters = useCallback((d: EmittersData) => {
    setEmitters({ ...d, confidence: computeConfidence(d) });
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:saveEmitters', {
      section: 'emitters',
      fields: Object.keys(d),
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand,
      systemDetails,
      water,
      emitters: d,
      dhwPressure,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [heatDemand, systemDetails, water, dhwPressure, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot, docId]);
  const saveDhwPressure = useCallback((d: DHWPressureData) => {
    setDhwPressure({ ...d, confidence: computeConfidence(d) });
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:saveDhwPressure', {
      section: 'dhw_pressure',
      fields: Object.keys(d),
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand,
      systemDetails,
      water,
      emitters,
      dhwPressure: d,
      priorities,
      futurePlans,
      urgency,
      disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [heatDemand, systemDetails, water, emitters, priorities, futurePlans, urgency, disruptionTolerance, selectedOptionId, explainers, scopeSnapshot, docId]);
  const savePriorities = useCallback((result: { priorities: Array<{ key: string; rank: number }>; urgency: string; disruptionTolerance: string; futurePlans: FuturePlan[] }) => {
    setPriorities(result.priorities);
    setUrgency(result.urgency);
    setDisruptionTolerance(result.disruptionTolerance);
    setFuturePlans(result.futurePlans);
    setActiveScreen('none');

    // Emit mapping event
    trackLifecycleEvent('section_saved', 'HubPage:savePriorities', {
      section: 'priorities',
      fields: ['upgradePriorities', 'futurePlans', 'urgency', 'disruptionTolerance'],
      docId: docId || '<local-draft>',
    });

    // Update raw input snapshot for devtools
    const rawInput = buildHubStorySnapshot({
      heatDemand,
      systemDetails,
      water,
      emitters,
      dhwPressure,
      priorities: result.priorities,
      futurePlans: result.futurePlans,
      urgency: result.urgency,
      disruptionTolerance: result.disruptionTolerance,
      selectedOptionId,
      selectedOptionAddOns: selectedOptionAddOns.filter(item => item.status === 'included').map(item => item.id),
      explainerStates: serializeExplainerState(explainers),
      scopeSnapshot,
    }).rawInput;
    updateRawInputSnapshot(rawInput as unknown as Record<string, unknown>, { docId: docId || undefined });
  }, [heatDemand, systemDetails, water, emitters, dhwPressure, futurePlans, selectedOptionId, explainers, scopeSnapshot, docId]);

  // Explainer handlers
  const handleExplainerView = useCallback((id: string) => {
    setExplainers(prev => markViewed(prev, id));
  }, []);
  const handleExplainerStar = useCallback((id: string) => {
    setExplainers(prev => markStarred(prev, id));
  }, []);
  const handleExplainerReport = useCallback((id: string) => {
    setExplainers(prev => markInReport(prev, id));
  }, []);


  const handleNavigateSource = useCallback((sourcePath: string) => {
    const map: Record<string, ActiveScreen> = {
      'hub://priorities': 'priorities',
      'hub://system-details': 'system-details',
      'hub://dhw-pressure': 'dhw-pressure',
      'hub://water': 'water',
      'hub://emitters': 'emitters',
    };
    const target = map[sourcePath];
    if (target) setActiveScreen(target);
  }, []);

  // Option selection - single selected option for add-on customisation and persistence
  const handleSelectOption = useCallback((id: string) => {
    if (selectedOptionA === id) {
      setSelectedOptionA(null);
      setSelectedOptionAddOns(getDefaultOptionalItems('complete'));
      return;
    }

    // Clear B or C if this card was already assigned there
    if (selectedOptionB === id) setSelectedOptionB(null);
    if (selectedOptionC === id) setSelectedOptionC(null);

    setSelectedOptionA(id);
    setSelectedOptionAddOns(getDefaultOptionalItems('complete'));
  }, [selectedOptionA, selectedOptionB, selectedOptionC]);

  const handleSelectOptionB = useCallback((id: string) => {
    // Clear A or C if this card was already assigned there
    if (selectedOptionA === id) {
      setSelectedOptionA(null);
      setSelectedOptionAddOns(getDefaultOptionalItems('complete'));
    }
    if (selectedOptionC === id) setSelectedOptionC(null);
    setSelectedOptionB(prev => prev === id ? null : id);
  }, [selectedOptionA, selectedOptionC]);

  const handleSelectOptionC = useCallback((id: string) => {
    // Clear A or B if this card was already assigned there
    if (selectedOptionA === id) {
      setSelectedOptionA(null);
      setSelectedOptionAddOns(getDefaultOptionalItems('complete'));
    }
    if (selectedOptionB === id) setSelectedOptionB(null);
    setSelectedOptionC(prev => prev === id ? null : id);
  }, [selectedOptionA, selectedOptionB]);

  const handleToggleAddOn = useCallback((optionId: string, addOnId: string) => {
    if (optionId !== selectedOptionA) return;
    setSelectedOptionAddOns(prev => toggleOptionalItem(prev, addOnId, selectedOptionA));
  }, [selectedOptionA]);

  const handleToggleOptional = useCallback((id: string) => {
    setOptionalItems(prev => toggleOptionalItem(prev, id));
  }, []);

  // Print report state
  const [showPrintView, setShowPrintView] = useState(false);

  const handlePrintReport = useCallback(() => {
    // Validate that we have something to print
    if (!selectedOption && !selectedOptionBCard && !selectedOptionCCard) {
      showToast('Please select at least one system option to print', 'error');
      return;
    }

    setShowPrintView(true);
    // Delay print to allow React to render the print view
    setTimeout(() => {
      try {
        window.print();
      } catch (err) {
        console.error('[hub] Print failed:', err);
        showToast('Print dialog could not be opened. Please try again or use your browser\'s print function.', 'error');
      } finally {
        // Return to normal view after a short delay
        setTimeout(() => setShowPrintView(false), 500);
      }
    }, 400);
  }, [selectedOption, selectedOptionBCard, selectedOptionCCard, showToast]);

  // If print view is active, render only the printable report
  if (showPrintView) {
    return (
      <PrintableAssessmentReportV2
        currentSystem={currentSystem}
        optionA={selectedOption}
        optionB={selectedOptionBCard}
        optionC={selectedOptionCCard}
        scope={scopeSnapshot}
        explainers={explainers}
        docId={docId}
        portalBaseUrl={window.location.origin}
        generatedAt={new Date().toISOString()}
        selectedOptionId={selectedOptionId}
        reportLayout="V2_DECISION"
        heatDemand={heatDemand}
      />
    );
  }

  return (
    <div className="hub-layout">
      <TopStrip
        docId={docId}
        canPublish={canPublish}
        engineStatus={engineStatus}
        isRunning={saving}
        runningLabel={saveStatusLabel}
        onRunReport={handleSave}
        onViewPortal={handleViewPortal}
        onPrintReport={handlePrintReport}
        canPrintReport={Boolean(selectedOption || selectedOptionBCard)}
      />

      {/* Validation errors panel */}
      {showValidationErrors && validationErrors.length > 0 && (
        <div className="hub-validation-panel" role="alert" style={{
          backgroundColor: '#fef2f2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          padding: '1rem',
          margin: '1rem 1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem', color: '#ef4444' }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#dc2626', fontSize: '1rem', fontWeight: 600 }}>
                Missing Required Information
              </h3>
              <p style={{ margin: '0 0 0.75rem 0', color: '#991b1b', fontSize: '0.875rem' }}>
                Please complete the following sections before running the report:
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#991b1b', fontSize: '0.875rem' }}>
                {validationErrors.map((error, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{error}</li>
                ))}
              </ul>
              <button
                onClick={() => setShowValidationErrors(false)}
                style={{
                  marginTop: '0.75rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page tabs */}
      <div className="hub-page-tabs">
        <button
          className={`hub-page-tab${activePage === 'system-choice' ? ' hub-page-tab--active' : ''}`}
          onClick={() => setActivePage('system-choice')}
        >
          1. System Choice
        </button>
        <button
          className={`hub-page-tab${activePage === 'explainers' ? ' hub-page-tab--active' : ''}`}
          onClick={() => setActivePage('explainers')}
        >
          2. Explainers
        </button>
      </div>

      <div className="hub-content">
        {/* Persistence status */}
        {(docId || saving) && (
          <div className="hub-persist-bar">
            <span className="hub-persist-bar__status">
              {saving ? 'Saving...' : lastSaved ? `Saved ${new Date(lastSaved).toLocaleTimeString()}` : 'Document loaded'}
            </span>
            {docId && <span className="hub-persist-bar__doc-id">{docId}</span>}
            <div className="hub-persist-bar__actions">
              <button className="hub-strip-btn" onClick={handleSave} disabled={saving}>
                {docId ? 'Update' : 'Save'}
              </button>
              <OpenPortalButton className="hub-strip-btn" onClick={handleViewPortal} disabled={saving}>Portal</OpenPortalButton>
              {selectedOption && (
                <button className="hub-strip-btn" onClick={handlePrintReport}>Print PDF</button>
              )}
            </div>
          </div>
        )}

        {/* ═══ PAGE 1: System Choice ═══ */}
        {activePage === 'system-choice' && (
          <>
            {/* Layer A: Comparison anchor — always visible */}
            <div className="site-visit-layer" ref={comparisonRef}>
              <p className="site-visit-layer__label">Current vs recommended</p>
              <ComparisonAnchor
                currentSystem={currentSystem}
                recommended={selectedOption}
                recommendedB={selectedOptionBCard}
                scope={scopeSnapshot}
              />
            </div>

            {/* Intake tiles */}
            <div className="site-visit-layer">
              <p className="site-visit-layer__label">Property & system intake</p>
              <ConfidenceLegend />
              <div className="hub-tiles">
                <HeatDemandTile data={heatDemand} onClick={() => setActiveScreen('heat-demand')} />
                <SystemDetailsTile data={systemDetails} onClick={() => setActiveScreen('system-details')} />
                <WaterTile data={water} onClick={() => setActiveScreen('water')} />
                <DHWPressureTile data={dhwPressure} onClick={() => setActiveScreen('dhw-pressure')} />
                <EmittersTile data={emitters} onClick={() => setActiveScreen('emitters')} />
              </div>
            </div>

            {/* Upgrade Brief — positioned before options to feel causal */}
            <div className="site-visit-layer">
              <p className="site-visit-layer__label">Upgrade brief</p>
              <div className="hub-tiles" style={{ gridTemplateColumns: '1fr' }}>
                <PrioritiesTile priorities={priorities} futurePlans={futurePlans} onClick={() => setActiveScreen('priorities')} />
              </div>
            </div>

            {/* System option cards — from engine when available */}
            {engineRunning && (
              <div className="hub-note" style={{ padding: '0.5rem 1rem', marginBottom: '0.5rem', opacity: 0.7 }}>
                Running recommendation engine...
              </div>
            )}

            {/* Engine summary panel — shows executive summary, confidence, red flags, and scenario sensitivity */}
            {engineResult && !engineRunning && (() => {
              const output = engineResult.output;
              const ds = output.decisionSummary;
              const hsf = output.homeSummaryFacts;
              const cs = output.confidenceSummary as ConfidenceSummary | undefined;
              const rf = output.redFlags as RedFlag[] | undefined;
              const wcc = output.whatCouldChangeRecommendation as string[] | undefined;

              const hasContent = ds || hsf || cs || (rf && rf.length > 0) || (wcc && wcc.length > 0);

              if (!hasContent) return null;

              return (
                <div className="hub-engine-summary">
                  {/* Decision summary — executive text */}
                  {ds && (ds.valueLine || ds.perfLine || ds.paragraph) && (
                    <div className="hub-engine-summary__section hub-engine-summary__decision">
                      <div className="hub-engine-summary__heading">Decision Summary</div>
                      {ds.paragraph && (
                        <p className="hub-engine-summary__paragraph">{ds.paragraph}</p>
                      )}
                      <div className="hub-engine-summary__metrics">
                        {ds.valueLine && <span className="hub-engine-summary__metric">{ds.valueLine}</span>}
                        {ds.perfLine && <span className="hub-engine-summary__metric">{ds.perfLine}</span>}
                        {ds.spaceLine && <span className="hub-engine-summary__metric">{ds.spaceLine}</span>}
                      </div>
                    </div>
                  )}

                  {/* Home summary facts */}
                  {hsf && (
                    <div className="hub-engine-summary__section hub-engine-summary__home">
                      <div className="hub-engine-summary__heading">Based on your home</div>
                      <div className="hub-engine-summary__facts">
                        {hsf.retrofitLevelLabel && <span className="hub-engine-summary__fact">Retrofit: {hsf.retrofitLevelLabel}</span>}
                        {hsf.emitterTypeLabel && <span className="hub-engine-summary__fact">Emitters: {hsf.emitterTypeLabel}</span>}
                        {hsf.lowTempReadinessLabel && <span className="hub-engine-summary__fact">Low-temp: {hsf.lowTempReadinessLabel}</span>}
                      </div>
                      {hsf.assumptionsUsed && hsf.assumptionsUsed.length > 0 && (
                        <ul className="hub-engine-summary__assumptions">
                          {hsf.assumptionsUsed.map((a: string, i: number) => <li key={i}>{a}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Confidence summary */}
                  {cs && (
                    <div className="hub-engine-summary__section hub-engine-summary__confidence">
                      <div className="hub-engine-summary__heading">
                        Recommendation Confidence:
                        <span className={`hub-engine-summary__confidence-badge hub-engine-summary__confidence-badge--${cs.level}`}>
                          {cs.level}
                        </span>
                      </div>
                      {cs.reasons.length > 0 && (
                        <ul className="hub-engine-summary__reasons">
                          {cs.reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      )}
                      {cs.toImprove.length > 0 && (
                        <div className="hub-engine-summary__improve">
                          <span className="hub-engine-summary__improve-label">To improve confidence:</span>
                          <ul>
                            {cs.toImprove.map((item, i) => (
                              <li key={i}><strong>{item.label}:</strong> {item.action}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Red flags */}
                  {rf && rf.length > 0 && (
                    <div className="hub-engine-summary__section hub-engine-summary__red-flags">
                      <div className="hub-engine-summary__heading">Flags</div>
                      {rf.map((flag, i) => (
                        <div key={i} className={`hub-engine-summary__flag hub-engine-summary__flag--${flag.severity}`}>
                          <span className="hub-engine-summary__flag-icon">{flag.severity === 'warning' ? '\u26A0' : '\u2139'}</span>
                          <div>
                            <strong>{flag.title}</strong>
                            <p>{flag.detail}</p>
                            {flag.mitigation && <p className="hub-engine-summary__mitigation">{flag.mitigation}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* What could change this recommendation */}
                  {wcc && wcc.length > 0 && (
                    <div className="hub-engine-summary__section hub-engine-summary__sensitivity">
                      <div className="hub-engine-summary__heading">What could change this recommendation</div>
                      <ul>
                        {wcc.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            <div ref={optionsRef}>
              <SystemOptionsArea
                cards={systemOptions}
                selectedOptionId={selectedOptionA}
                selectedOptionIdB={selectedOptionB}
                selectedOptionIdC={selectedOptionC}
                onSelect={handleSelectOption}
                onSelectB={handleSelectOptionB}
                onSelectC={handleSelectOptionC}
                selectedOptionAddOns={selectedOptionAddOns}
                onToggleAddOn={handleToggleAddOn}
                onNavigateSource={handleNavigateSource}
              />
            </div>

            {/* Cost charts — from engine when available */}
            {engineResult && !engineRunning && (
              <div className="site-visit-layer" style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <p className="site-visit-layer__label" style={{ margin: 0 }}>Upgrade comparison view</p>
                  <button
                    className="hub-strip-btn"
                    onClick={() => setShowUpgradeComparison((prev) => !prev)}
                    type="button"
                  >
                    {showUpgradeComparison ? 'Hide comparison' : 'Show comparison'}
                  </button>
                </div>
                {showUpgradeComparison && <CostChartsPanel engineResult={engineResult} />}
              </div>
            )}

            {/* Layer B: Explainer cards — pulled, not pushed */}
            <div className="site-visit-layer" style={{ marginTop: 24 }}>
              <p className="site-visit-layer__label">
                Explainers
                <span className="site-visit-layer__count">
                  {starredExplainers.length} marked
                </span>
              </p>
              <div className="explainer-cards">
                {explainers.map(exp => (
                  <ExplainerCard
                    key={exp.id}
                    explainer={exp}
                    onView={handleExplainerView}
                    onStar={handleExplainerStar}
                    onReport={handleExplainerReport}
                  />
                ))}
              </div>
            </div>

            {/* Selected options summary and proceed */}
            {(selectedOption || selectedOptionBCard || selectedOptionCCard) && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  {selectedOption && (
                    <div className="hub-note" style={{ padding: '0.4rem 0.8rem', flex: '1 1 auto' }}>
                      <strong>Option A:</strong> {selectedOption.label} ({formatOptionMatchScore(selectedOption)})
                      {selectedOptionAddOns.filter(a => a.status === 'included').length > 0 && (
                        <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#6366f1' }}>
                          +{selectedOptionAddOns.filter(a => a.status === 'included').length} add-on(s)
                        </span>
                      )}
                    </div>
                  )}
                  {selectedOptionBCard && (
                    <div className="hub-note" style={{ padding: '0.4rem 0.8rem', flex: '1 1 auto' }}>
                      <strong>Option B:</strong> {selectedOptionBCard.label} ({formatOptionMatchScore(selectedOptionBCard)})
                    </div>
                  )}
                  {selectedOptionCCard && (
                    <div className="hub-note" style={{ padding: '0.4rem 0.8rem', flex: '1 1 auto' }}>
                      <strong>Option C:</strong> {selectedOptionCCard.label} ({formatOptionMatchScore(selectedOptionCCard)})
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="hub-strip-btn hub-strip-btn--primary"
                    onClick={() => setActivePage('explainers')}
                  >
                    Open Explainers &rarr;
                  </button>
                  <button className="hub-strip-btn" onClick={handlePrintReport}>
                    Print PDF Report
                  </button>
                  {!docId && (
                    <button className="hub-strip-btn" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save draft'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ PAGE 2: Explainers ═══ */}
        {activePage === 'explainers' && (
          <ExplainersPage
            explainers={explainers}
            highlightedExplainerId={searchParams.get('explainer') ?? undefined}
            onView={handleExplainerView}
            onStar={handleExplainerStar}
            onReport={handleExplainerReport}
          />
        )}
      </div>

      {/* Edit screens (modal overlays) */}
      {activeScreen === 'heat-demand' && (
        <HeatDemandScreen data={heatDemand} onSave={saveHeatDemand} onClose={() => setActiveScreen('none')} />
      )}
      {activeScreen === 'system-details' && (
        <SystemDetailsScreen data={systemDetails} onSave={saveSystemDetails} onClose={() => setActiveScreen('none')} />
      )}
      {activeScreen === 'water' && (
        <WaterScreen data={water} onSave={saveWater} onClose={() => setActiveScreen('none')} />
      )}
      {activeScreen === 'dhw-pressure' && (
        <DHWPressureScreen data={dhwPressure} onSave={saveDhwPressure} onClose={() => setActiveScreen('none')} />
      )}
      {activeScreen === 'emitters' && (
        <EmittersScreen data={emitters} onSave={saveEmitters} onClose={() => setActiveScreen('none')} />
      )}
      {activeScreen === 'priorities' && (
        <PrioritiesScreen initialPriorities={priorities} initialUrgency={urgency} initialDisruptionTolerance={disruptionTolerance} initialFuturePlans={futurePlans} onSave={savePriorities} onCancel={() => setActiveScreen('none')} />
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`hub-toast hub-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Scroll navigation */}
      {showScrollNav && activePage === 'system-choice' && (
        <div className="hub-scroll-nav">
          <button
            className="hub-scroll-nav__btn"
            onClick={() => comparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            &#8593; Back to comparison
          </button>
          {optionsRef.current && (
            <button
              className="hub-scroll-nav__btn hub-scroll-nav__btn--primary"
              onClick={() => optionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              View options
            </button>
          )}
        </div>
      )}

    </div>
  );
}
